import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { ArrowLeft, Lock, Pencil, Printer, Trash2, CheckCircle2, Send, RotateCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ColumnCountToggle } from "@/components/portal/tables/ColumnCountToggle";
import { FormFieldRow } from "@/components/portal/tables/FormFieldRow";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { renderField } from "@/lib/myapp/field-renderer";
import { fieldDecimalPrecisionError } from "@/lib/myapp/numeric-input-step";
import { canAddRecords, manualEntryBlockedReason } from "@/lib/myapp/tableCapabilities";
import { StateTransitionBar } from "@/components/portal/tables/state-machine/StateTransitionBar";
import { StateBadge } from "@/components/portal/tables/state-machine/StateBadge";
import { StateProgressBar } from "@/components/portal/tables/state-machine/StateProgressBar";
import { RecordActionRow } from "@/components/portal/tables/state-machine/RecordActionRow";
import { saveAndTransition } from "@/lib/myapp/save-and-transition";
import { buildSaveAndTransitionDeps } from "@/lib/myapp/build-save-and-transition-deps";
import { extractRecordState } from "@/lib/myapp/extract-record-state";
import {
  resolvePreAdvanceReview,
  type PreAdvanceReviewProps,
} from "@/components/portal/tables/state-machine/preAdvanceReview";
import {
  hydrateRowToFormData,
  mergeConflictRowIntoFormData,
} from "@/lib/myapp/edit-hydration";
import {
  fkWriteKey,
  getSharedFkCollisionColumns,
  isSharedFkCollisionField,
  stripUntouchedCollisionFields,
} from "@/lib/myapp/shared-fk-collision";
import { SharedFkCollisionNotice } from "@/components/portal/tables/SharedFkCollisionNotice";
import { deriveInitialStateAsCurrent } from "@/lib/myapp/initial-state";
import { useMissingRequiredDerivation } from "@/lib/myapp/use-missing-required";
import { validateSubmitSearch } from "@/lib/myapp/submit-search";
import { useSubmitLinkLoader } from "@/lib/myapp/use-submit-link-loader";
import { evaluateFormula as evaluateFormulaFn, evaluateConcatenation as evaluateConcatenationFn, evaluateAllCalculations, buildFieldValueMap } from "@/lib/myapp/formula-evaluator";
import { diffSubmitData } from "@/lib/myapp/submit-diff";
import { evaluateCalculatedField, evaluateIr, isComputedOnSave, type CalculatedFieldDisplay } from "@/lib/myapp/tsql-ir";
import {
  findRequiredAttachmentMissesForRecord,
  formatAttachmentMisses,
} from "@/lib/myapp/required-attachment-misses";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  myappApi,
  asRecordChanged,
  VERSION_TOKEN_KEY,
} from "@/services/api/myapp";
import { tokenAdoptionDecision } from "@/lib/myapp/versionToken";
import { showRecordConflictToast } from "@/components/portal/tables/RecordConflictToast";
import { useAuth } from "@/contexts/AuthContext";
import { PermissionDenied } from "@/components/ui/permission-denied";
import { useMyAppPermissions } from "@/hooks/useMyAppPermissions";
import { useTableMetadata } from "@/hooks/useTableMetadata";
import { useFormDraft } from "@/hooks/useFormDraft";
import { useColumnPreference } from "@/hooks/useColumnPreference";
import { usePortalData } from "@/components/portal/PortalDataProvider";
import { relativeFromNow } from "@/lib/relative-time";
import type { FormField, LinkedOption } from "@/types/myapp";
import { HeaderLineForm } from "@/components/portal/tables/composite";
import { SignatureFieldManager } from "@/components/portal/tables/SignatureFieldManager";
import { AttachmentFieldManager } from "@/components/portal/tables/AttachmentFieldManager";
import {
  ConnectorFilesCell,
} from "@/components/portal/tables/ConnectorFilesCell";
import { FILES_BINDING_ERROR, connectorBindingState } from "@/lib/myapp/connector-binding-state";
import {
  useConnectorAttachmentBinding,
  useConnectorAttachmentSource,
} from "@/hooks/useConnectorAttachments";

// Direct data-ENTRY guard for a decimal/money field's value (grid.tsx's
// NumericCell has the same guard for inline cell editing, HeaderLineForm.tsx
// and respond.index.tsx share the same guard via
// `fieldDecimalPrecisionError`) — Allan's gate-review call, 2026-10-01:
// rather than trying to preserve arbitrary precision through the float/JSON
// write path, reject a typed value with more decimal digits than the
// field's effective precision outright. Never applied to the Decimal Places
// SETTING control itself (field designer edit.tsx), only to entering a
// VALUE into an existing decimal/money field. See
// `fieldDecimalPrecisionError` in numeric-input-step.ts for the ceiling
// logic (decimal widens with `decimal_places`, money stays fixed at 4).
const decimalPrecisionError = fieldDecimalPrecisionError;

export const Route = createFileRoute("/portal/myapp/$tableId/submit")({
  component: FormSubmitPage,
  validateSearch: validateSubmitSearch,
});

function FormSubmitPage() {
  const { tableId } = Route.useParams();
  const navigate = useNavigate();
  const { token, refreshSession, user } = useAuth();
  const { selectedOrganisation } = usePortalData();
  // Mirror the live token in a ref so the long-lived `fetchData` callback (and
  // its transitive callbacks) can read a fresh bearer credential without `token`
  // sitting in their dependency arrays. A silent JWT refresh changes `token`;
  // were it a dep, `fetchData` would be recreated, its effect would re-run and
  // wipe an in-progress new record. Same idiom as `startInViewRef` in
  // HeaderLineForm. localStorage already holds the refreshed token before the
  // refresh event fires, so the ref always reads the current value.
  const tokenRef = useRef(token);
  useEffect(() => {
    tokenRef.current = token;
  }, [token]);
  const { edit: editId, mode, from } = Route.useSearch();
  const isEditMode = Boolean(editId);
  const formId = tableId;

  // Per-user, browser-local choice of how many columns the fields lay across
  // (desktop only; collapses to 1 column on mobile). Declared up here with the
  // other hooks so it runs before any early return (rules of hooks).
  const [formColumns, setFormColumns] = useColumnPreference({
    tableId: formId,
    surface: "form",
    max: 4,
  });

  // View mode: existing records open read-only by default.
  // Pencil icon or Edit button switches to edit mode via ?mode=edit.
  const [viewMode, setViewMode] = useState(isEditMode && mode !== "edit");

  // Object-level permissions — API is single source of truth
  const { permissions: objPerms, loading: permsLoading, denied: permsDenied } = useMyAppPermissions(formId);
  const canDelete = objPerms?.can_delete ?? false;

  // Form metadata + workflow states come from the shared Phase B cache
  // (see useTableMetadata). On revisit within the 5-min staleTime the
  // hook returns synchronously from the TanStack Query cache, so the
  // "Loading table..." screen below only shows on a true cold mount.
  // refetchMetadata is wired into the Retry button below.
  const {
    form,
    tableStates,
    loading: metadataLoading,
    error: metadataError,
    refetch: refetchMetadata,
  } = useTableMetadata(formId, { enabled: !!formId });
  // A virtual table's rows come from its source query: an existing record
  // opens read-only (even with ?mode=edit) for anyone who may view the table,
  // with no Edit and no Delete. This is presentation, not the guard: dp's
  // DataService._assert_not_virtual refuses create_record, update_record,
  // delete_record, update_records_batch and delete_records_batch on a
  // virtual table.
  const isVirtual = form?.table_type === "virtual";
  const isViewing = isEditMode && (viewMode || isVirtual);
  const permissionDenied =
    permsDenied ||
    (objPerms !== null &&
      (isEditMode
        ? !(isVirtual ? objPerms.can_view : objPerms.can_edit)
        : !objPerms.can_submit));
  // A connector_files field (virtual tables only) shows the record's files
  // through the same popover as the table's Files cell.
  const hasFilesField = !!form?.fields.some((f) => f.field_type?.code === "connector_files");
  const {
    binding: connectorBinding,
    pending: connectorBindingPending,
    failed: connectorBindingFailed,
  } = connectorBindingState(
    useConnectorAttachmentBinding(formId, {
      enabled: isVirtual && hasFilesField && !!objPerms?.can_view,
    }),
  );
  const connectorAttachmentSource = useConnectorAttachmentSource(formId);
  // `loading` gates the full-page "Loading table..." replacement and is
  // only true on the very first record fetch. Subsequent refetches
  // (after a save or transition) use `refreshing` instead — the record
  // stays visible and a thin progress bar shows at the top of the form.
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const isInitialFetchRef = useRef(true);
  // Which editId the current `formData` snapshot was hydrated for — set at
  // the point fetchData actually calls setFormData (see below). Lets the
  // lookupValues seed effect tell "formData is fresh for this record" from
  // "still holding a previous record's data mid-refetch", without relying
  // on the PK column (which isn't in formDetail.fields and so never lands
  // in formData at all).
  const hydratedForEditIdRef = useRef<number | undefined>(undefined);
  // Bumped at the start of every fetchData call; a call whose generation no
  // longer matches by the time its await resolves has been superseded by a
  // newer one (rapid record-to-record navigation with overlapping in-flight
  // requests) and must not apply its data — an older response landing after
  // a newer one otherwise produces a permanent mixed-record display (some
  // state from record A, some from record B), found in gate-review.
  const fetchGenerationRef = useRef(0);
  const [saving, setSaving] = useState(false);
  /**
   * True while a post-review reconcile read is in flight.
   *
   * 🔴 THE CONFIRM EXIT HAS NO RESPONSE TO ADOPT FROM, SO IT MUST BE GATED.
   * `handleSubmit` takes its fresh token straight off the PUT response, which
   * closes the window for every path that SAVES. The Confirm path does not
   * save -- it transitions, and dp's `transition_record` stamps
   * `[SysUpdatedAtUtc] = GETUTCDATE()` (`origin/main` 87d6e5130,
   * `src/myapp/services/v1/state_service.py:805`) but returns a bare status
   * dict with no row on it. There is therefore NOTHING to read the new token
   * off; the only way to learn it is the re-read, and until that re-read
   * lands the form is holding a token the server has already moved past.
   * Measured at gate round 4: Advance to Push → Confirm → edit → Update
   * Record sent the pre-transition token and 409'd.
   *
   * So rather than race the read, we disable the save/advance buttons until
   * it settles. Inputs stay live on purpose -- locking fields mid-keystroke
   * to win a race the person cannot see is worse than the wait, and blocking
   * the SAVE is what actually prevents the stale write.
   */
  const [reconciling, setReconciling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<Record<string, unknown>>({});
  // Optimistic-concurrency state (plan D21). `recordVersionRef` is the
  // SysUpdatedAtUtc this record was READ with — sent on save so a write
  // that would silently overwrite somebody else's is refused instead.
  // `loadedDataRef` is the hydrated snapshot it was read with, which is how
  // a conflict refresh tells "the user typed here" from "this field is just
  // showing what was loaded" without a general touched-field tracker.
  const recordVersionRef = useRef<string | null>(null);
  const loadedDataRef = useRef<Record<string, unknown>>({});
  /**
   * Monotonic counter over the HELD version token. Bumped every time
   * `recordVersionRef` changes, and every time the record identity changes.
   *
   * 🔴 THIS IS THE ORDERING, NOT THE TIMESTAMP. The previous guard compared
   * `Date.parse` of the incoming token against the held one and dropped
   * anything older. That premise -- "a token never goes backwards" -- is only
   * true WITHIN one record: load a different record and its SysUpdatedAtUtc is
   * whatever it is, routinely older than the row just edited, and the guard
   * would have silently refused to adopt it, sending a foreign token on the
   * next save. What actually needs ordering is issue-time vs landing-time of
   * a fire-and-forget read, which is what an epoch captures exactly.
   */
  const versionEpochRef = useRef(0);
  /**
   * Adopt the version token off any row the server has just handed back.
   *
   * 🔴 THE SAVE ITSELF CARRIES THE NEW TOKEN — READ IT OFF THE RESPONSE. Every
   * write endpoint here ends `record = await self.get_record(...)` /
   * `return record` (dp `origin/main` 87d6e5130,
   * `src/myapp/services/v1/data_service.py:8641`), so a PUT answers with
   * exactly the row a GET would. Discarding that response and re-reading the
   * record afterwards is what opened the window this helper closes: the
   * reviewed advance saves with `skipRefetch: true` (the review must not race
   * a form re-read), so between the save landing and any later GET resolving
   * the form was fully interactive holding a token the server had already
   * moved past — a second save inside that window 409'd and named the person
   * themselves as the one who changed it.
   *
   * 🔴 A READ PASSES THE EPOCH IT WAS ISSUED AT; A WRITE RESPONSE DOES NOT.
   * A write response is authoritative the moment it lands -- it IS the newest
   * state, so it adopts unconditionally. A read is only authoritative if
   * nothing has changed the held token since it was issued; if the epoch has
   * moved, a save (or a newer read) landed first and this one is stale, so it
   * is dropped rather than regressing the ref and 409ing the next save.
   */
  const adoptVersionToken = useCallback(
    (record: unknown, issuedAtEpoch?: number) => {
      const raw = (record as Record<string, unknown> | null | undefined)?.[
        VERSION_TOKEN_KEY
      ];
      // The ordering rule itself lives in `lib/myapp/versionToken` so it can be
      // tested directly — a gate round found it was pinned by nothing here.
      if (
        tokenAdoptionDecision(raw, issuedAtEpoch, versionEpochRef.current) !==
        "adopt"
      )
        return;
      recordVersionRef.current = raw as string;
      versionEpochRef.current += 1;
    },
    [],
  );
  // Mirrors `formData` so async callbacks can read the LATEST value rather
  // than the one captured when they were built. The post-transition
  // reconcile resolves after the success toast, by which point the user may
  // be typing again; a closure read would silently discard that.
  const formDataRef = useRef(formData);
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Linked field options
  const [linkedOptions, setLinkedOptions] = useState<
    Record<string, LinkedOption[]>
  >({});
  const [loadingLinkedOptions, setLoadingLinkedOptions] = useState<
    Record<string, boolean>
  >({});

  // Lookup field values (keyed by lookup field id)
  const [lookupValues, setLookupValues] = useState<
    Record<string, unknown>
  >({});

  // Cache for linked table field definitions (keyed by table id)
  const [linkedTableFields, setLinkedTableFields] = useState<
    Record<string, FormField[]>
  >({});

  // Inherited display values (keyed by column name)
  const [inheritedDisplayValues, setInheritedDisplayValues] = useState<
    Record<string, string>
  >({});

  // Link field display values (keyed by column name)
  // Tracks the display text when user selects from link dropdowns
  const [linkDisplayValues, setLinkDisplayValues] = useState<
    Record<string, string>
  >({});

  // Raw per-field display snapshot (row[col]) for link fields, hydrated from
  // the API. The only reliable per-field source for shared-FK collision
  // fields — see edit-hydration.ts (HydratedRow.linkSnapshot) and
  // lib/myapp/shared-fk-collision.ts.
  const [linkSnapshotValues, setLinkSnapshotValues] = useState<
    Record<string, string>
  >({});
  // Backward-repair (always-editable model): collided link fields are NEVER
  // locked. The stored snapshot is gold and is left untouched unless the user
  // ACTIVELY re-selects a value for that field. This set tracks which collided
  // columns the user touched this edit; only those persist (the rest are
  // stripped from the payload so their snapshot + per-field SysFk stay as-is).
  const [touchedCollisionCols, setTouchedCollisionCols] = useState<Set<string>>(
    () => new Set(),
  );

  // Success state (for new record creation)
  const [submitted, setSubmitted] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  // State-machine context — populated from record._state when the table has
  // states defined. Null = no state machine on this table.
  const [recordState, setRecordState] =
    useState<import("@/types/myapp").RecordStateContext | null>(null);

  /**
   * Re-read this record's state context after a review has transitioned it.
   * The plain path gets this from `saveAndTransition`'s
   * `reconcileInBackground`; a review performs its own transitions, so it
   * needs the same refresh. Errors are swallowed — a stale badge beats a toast
   * about a read nobody asked for.
   */
  const refreshRecordState = useCallback(async () => {
    if (!editId) return;
    // Raised SYNCHRONOUSLY, before the first await, so `void
    // refreshRecordState()` leaves no un-gated tick for a click to land in.
    setReconciling(true);
    const issuedAtEpoch = versionEpochRef.current;
    try {
      const record = await myappApi.records.get(token, formId, Number(editId));
      // 🔴 ADOPT THE VERSION TOKEN, exactly as the plain transition path does
      // above. `transition_record` writes `SysUpdatedAtUtc` on every hop, so a
      // form left open after a reviewed advance holds a token the server has
      // already moved past: the person's NEXT save 409s and the conflict names
      // them as the one who changed it. Caught at gate, 2026-09-22 — the plain
      // path had this and the reviewed path did not.
      //
      // 🔴 FOR THE EXITS THAT SAVED, THIS IS A BACKSTOP. FOR CONFIRM IT IS THE
      // ONLY SOURCE. `handleSubmit` adopts the token off its own PUT response,
      // so Close and the reviewed advance are already fresh before any exit
      // runs. Confirm never saves — it transitions, and a transition answers
      // with a bare status dict and no row — so there is nothing to adopt from
      // and this read is the only way to learn the token it stamped. That is
      // why `reconciling` gates the save buttons until this resolves: on that
      // path there is no fresher value to fall back on.
      //
      // Guarded by the epoch it was ISSUED at, so a read overtaken by a save
      // (or by the form moving to another record) is dropped rather than
      // regressing the ref and 409ing the next save.
      // 🔴 THE STATE GOES WITH THE TOKEN OR NOT AT ALL. Dropping a stale read's
      // token while still applying its `_state` is worse than either choice
      // alone: the badge and the available transitions would come from a row
      // this form is no longer showing. The form only changes record by a load,
      // and a load bumps the epoch, so "the epoch moved" is exactly the
      // condition under which this payload describes somebody else's record.
      if (issuedAtEpoch !== versionEpochRef.current) return;
      adoptVersionToken(record, issuedAtEpoch);
      setRecordState(
        extractRecordState(record as Record<string, unknown> | null | undefined),
      );
    } catch {
      /* intentionally swallowed — see above */
    } finally {
      // Lowered on the error path too: a swallowed read must not leave the
      // person staring at permanently dead buttons.
      setReconciling(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editId, formId, token, adoptVersionToken]);

  /**
   * A pre-advance review the person has to read before the transition happens.
   *
   * 🔴 THE ADVANCE IS STILL MyApp's OWN. This is not a second control and not
   * a second write path: `StateTransitionBar` is the button, the review is a
   * dialog in front of it, and what the review does on confirm is the same
   * `POST /records/{id}/transition` the plain path does. Every table without a
   * registered review — which is every table but one — goes straight through
   * `handleSaveAndTransition` exactly as it always has. See
   * `preAdvanceReview.ts` for why the registry has one entry and must not grow
   * a generic MyApp capability.
   */
  const [pendingReview, setPendingReview] = useState<{
    Review: React.ComponentType<PreAdvanceReviewProps>;
    targetStateId: string;
    closeAfter: boolean;
  } | null>(null);

  /**
   * Advance this record, through a pre-advance review if one is registered.
   *
   * 🔴 THERE ARE THREE ADVANCE CONTROLS ON THIS FORM AND THEY ALL COME HERE.
   * `StateTransitionBar` at the top, and `RecordActionRow`'s "Advance to X" and
   * "Advance to X and Close" in the footer. Wiring only the bar would leave two
   * buttons that move a Price Book row into `Push` with no guardrail list and
   * no overrides, and that push would go out looking exactly like a reviewed
   * one. A review that covers one door of three is not a review.
   *
   * No review registered → `handleSaveAndTransition`, unchanged. That is every
   * other MyApp table.
   */
  const advanceWithReview = async (
    targetStateId: string,
    closeAfter: boolean,
  ): Promise<void> => {
    // The registry is keyed on the state NAME — it survives a table rebuild and
    // it is what a person types in the portal — and MyApp has already told us
    // which transitions this record has, so the name comes from the button that
    // was pressed rather than a second lookup.
    const target = recordState?.available_transitions.find(
      (t) => t.id === targetStateId,
    );
    // 🔴 THE ORG COMES FROM THE TABLE, NEVER FROM THE SIDEBAR'S SELECTION.
    // `selectedOrganisation` is the org SWITCHER's state, and for anyone who
    // belongs to more than one org it can differ from the org that owns the
    // record on screen — a platform admin's switcher lands on whichever org
    // sorts first. Measured on the dev stack 2026-09-22: opening the Price
    // Book with the switcher on another org resolved NO review, so pressing
    // "Advance to Push" moved the row into Push with no guardrail list, no
    // overrides and no note — an unreviewed push indistinguishable from a
    // reviewed one, which is the exact failure this review exists to stop.
    // `form.organisation_id` is MyApp's own answer for THIS table and cannot
    // drift from the record being edited.
    const Review = resolvePreAdvanceReview(
      form?.organisation_id,
      formId,
      target?.name,
    );
    if (!Review) {
      await handleSaveAndTransition({ targetStateId, closeAfter });
      return;
    }
    // 🔴 SAVE FIRST, THEN REVIEW. The review reads the price check off the
    // database, so it must read what the person is looking at — an unsaved edit
    // would be reviewed against the previous prices and pushed as the new ones.
    // Same save `saveAndTransition` performs before its own transition; if it
    // refuses (a required field, a line miss) it has already said why, and no
    // review opens.
    const saved = await handleSubmit({ closeAfter: false, skipRefetch: true });
    if (!saved) return;
    setPendingReview({ Review, targetStateId, closeAfter });
  };
  const lockedFieldSet = useMemo(
    () => new Set(recordState?.locked_fields ?? []),
    [recordState],
  );
  // Link columns that share one physical FK column with another link field on
  // this table (2+ links to the same linked table). Their FK/_display value is
  // an unreliable last-write-wins shared value, so we display each field's own
  // raw snapshot and persist a collided field ONLY when the user re-selects it
  // (see touchedCollisionCols + stripUntouchedCollisionFields). The stored
  // snapshot is gold and is never machine-altered. See
  // lib/myapp/shared-fk-collision.ts. Empty set for tables without the pattern,
  // so non-collided tables are completely unaffected.
  const sharedFkCollisionCols = useMemo(
    () => getSharedFkCollisionColumns(form?.fields ?? []),
    [form?.fields],
  );
  // Client-side derivation of which fields block Save / Advance. Mirrors
  // the backend's _check_state_lockdown and _check_required_fields_for_transition
  // so the buttons reflect live form edits without waiting for a save
  // round-trip. See the hook's docstring for the full contract.
  const {
    missingRequiredFieldNames,
    saveBlockedFieldNames,
    requiredNowFieldIds,
    emptyRequiredNowFieldIds,
  } = useMissingRequiredDerivation({
    fields: form?.fields,
    formData,
    tableStates,
    recordState,
    // Collided fields are always editable now, so required validation fires on
    // them normally (the hydrated value/snapshot satisfies "has a value").
    lockedColumns: undefined,
  });
  // For new records on a stated table, the backend will stamp the
  // initial state on insert (data_service.create_record). We pre-render
  // the same state in the progress bar so the user can see "this record
  // will start in <X>" before submitting.
  const initialStateAsCurrent = useMemo(
    () => deriveInitialStateAsCurrent(tableStates),
    [tableStates],
  );

  const stateById = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of tableStates) m.set(s.id, s.name);
    if (recordState) m.set(recordState.current_state.id, recordState.current_state.name);
    return m;
  }, [recordState, tableStates]);

  // Delete confirmation
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Submit guard evaluation — checks if status changes are allowed
  const submitGuardResult = useMemo(() => {
    if (!form) return { blocked: false, message: "" };
    const { submit_guard_status_field_id, submit_guard_field_id, submit_guard_min, submit_guard_max } = form;
    if (!submit_guard_status_field_id || !submit_guard_field_id) return { blocked: false, message: "" };

    const statusField = form.fields.find((f) => f.id === submit_guard_status_field_id);
    const guardField = form.fields.find((f) => f.id === submit_guard_field_id);
    if (!statusField || !guardField) return { blocked: false, message: "" };

    // Check if the status field is set to a non-default value
    const statusValue = formData[statusField.column_name];
    const defaultValue = statusField.default_value;
    if (!statusValue || statusValue === defaultValue || statusValue === "") {
      return { blocked: false, message: "" };
    }

    // Get the guard field value — may be a calculation/rollup stored in formData
    let guardValue: number | null = null;
    const raw = formData[guardField.column_name];
    if (raw !== null && raw !== undefined && raw !== "") {
      const num = Number(raw);
      if (!isNaN(num)) guardValue = num;
    }
    // Also check if it's a calculation field and evaluate it. v2 NEVER
    // falls back to evaluateFormulaFn — it only understands the legacy
    // mini-language, and running it over T-SQL text produces a wrong-but-
    // plausible number (mirrors HeaderLineForm.tsx's identical guard).
    // NOT_MIRRORED (no browser mirror yet) is treated as "no value", same
    // as the generic message below.
    if (guardValue === null && guardField.field_type?.code === "calculation") {
      let calc: string | number | null = null;
      if (guardField.calculation_spec_version === 2) {
        const irResult = evaluateIr(
          guardField.calculation_ir,
          buildFieldValueMap(form.fields, formData, lookupValues),
        );
        calc = isComputedOnSave(irResult) ? null : irResult;
      } else if (guardField.calculation_formula) {
        calc = evaluateFormulaFn(guardField.calculation_formula, form.fields, formData, lookupValues);
      }
      if (calc !== null && !isNaN(Number(calc))) guardValue = Number(calc);
    }

    if (guardValue === null) {
      return {
        blocked: true,
        message: `Cannot change ${statusField.name} — ${guardField.name} has no value yet.`,
      };
    }

    if (submit_guard_min !== null && guardValue < submit_guard_min) {
      return {
        blocked: true,
        message: `Cannot change ${statusField.name} — ${guardField.name} (${guardValue}) must be at least ${submit_guard_min}.`,
      };
    }
    if (submit_guard_max !== null && guardValue > submit_guard_max) {
      return {
        blocked: true,
        message: `Cannot change ${statusField.name} — ${guardField.name} (${guardValue}) must be at most ${submit_guard_max}.`,
      };
    }

    return { blocked: false, message: "" };
  }, [form, formData, lookupValues]);

  // Pending signature blobs for new record creation (fieldId -> blobRef)
  const pendingSignaturesRef = useRef<Map<string, React.MutableRefObject<Blob | null>>>(new Map());
  const getOrCreatePendingBlobRef = useCallback((fieldId: string) => {
    let ref = pendingSignaturesRef.current.get(fieldId);
    if (!ref) {
      ref = { current: null };
      pendingSignaturesRef.current.set(fieldId, ref);
    }
    return ref;
  }, []);

  // Pending attachment files for new record creation (fieldId -> filesRef)
  const pendingAttachmentsRef = useRef<Map<string, React.MutableRefObject<File[]>>>(new Map());
  const getOrCreatePendingAttachmentsRef = useCallback((fieldId: string) => {
    let ref = pendingAttachmentsRef.current.get(fieldId);
    if (!ref) {
      ref = { current: [] };
      pendingAttachmentsRef.current.set(fieldId, ref);
    }
    return ref;
  }, []);

  // --- Layer 2: durable draft autosave (survives F5 / tab close / crash) ---
  // Layer 1 (PR #477) keeps in-progress input alive in React state across token
  // refresh + window-focus refetch; this persists it to localStorage too, and
  // offers it back on next open. Header tables delegate to HeaderLineForm, which
  // owns its own composite draft — so this is disabled for them.
  // See plans/open/myapp-capture-forms-layer-2-autosave.md.
  const draftExcludeFields = useMemo(
    () =>
      (form?.fields ?? [])
        .filter(
          (f) =>
            f.field_type?.code === "signature" ||
            f.field_type?.code === "attachment",
        )
        .map((f) => f.column_name),
    [form?.fields],
  );
  const draftEnabled =
    !!form &&
    !form.is_header_table &&
    !loading &&
    !isViewing &&
    !permissionDenied &&
    !submitted &&
    !!user?.email &&
    !!selectedOrganisation?.organisationId;
  const {
    pendingDraft,
    restore: restoreDraft,
    dismiss: dismissDraft,
    clear: clearDraft,
    isDirty: draftIsDirty,
  } = useFormDraft(
    {
      userEmail: user?.email ?? "",
      orgId: selectedOrganisation?.organisationId ?? "",
      tableId: formId,
      mode: isEditMode ? "edit" : "new",
      editId: editId != null ? String(editId) : undefined,
      enabled: draftEnabled,
      excludeFields: draftExcludeFields,
      // Persist only the fields the user moved; see HeaderLineForm for why.
      serverBaseline: isEditMode ? loadedDataRef.current : null,
    },
    formData,
  );

  // Warn before a hard unload (refresh / tab close) while there is unsaved input.
  useEffect(() => {
    if (!draftIsDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [draftIsDirty]);

  // Apply a restored draft to the form. The hook is storage-only; the caller
  // owns form state (per its contract), so we merge the draft over current
  // defaults — excluded (file/signature) columns keep their defaults.
  const handleRestoreDraft = useCallback(() => {
    const draft = restoreDraft();
    if (!draft) return;
    setFormData((prev) => ({ ...prev, ...draft.formData }));
    if (draft.excludedFields && draft.excludedFields.length > 0) {
      toast.info(
        "Draft restored. Re-attach any files or signatures — those can't be saved as drafts.",
      );
    } else {
      toast.success("Draft restored");
    }
  }, [restoreDraft]);

  const fetchLinkedOptions = useCallback(
    async (field: FormField, filterColumn?: string, filterValue?: number) => {
      if (!field.linked_table_id) return;

      setLoadingLinkedOptions((prev) => ({ ...prev, [field.id]: true }));
      try {
        const options = await myappApi.records.getLinkedOptions(
          tokenRef.current,
          formId,
          field.linked_table_id,
          {
            displayFieldId: field.linked_display_field_id || undefined,
            filterColumn,
            filterValue,
          }
        );
        setLinkedOptions((prev) => ({ ...prev, [field.id]: options }));
      } catch (err) {
        console.error("Failed to load linked options:", err);
      } finally {
        setLoadingLinkedOptions((prev) => ({ ...prev, [field.id]: false }));
      }
    },
    [formId]
  );

  // Refetch cascading child options when parent value changes
  const refreshCascadingOptions = useCallback(
    (parentFieldId: string, parentValue: number | null) => {
      if (!form) return;

      // Find all fields that cascade from this parent
      const childFields = form.fields.filter(
        (f) => f.cascade_parent_field_id === parentFieldId
      );

      childFields.forEach((childField) => {
        if (parentValue !== null && childField.cascade_filter_column) {
          // Refetch with filter
          fetchLinkedOptions(childField, childField.cascade_filter_column, parentValue);
        } else {
          // Clear options if no parent value
          setLinkedOptions((prev) => ({ ...prev, [childField.id]: [] }));
        }
      });
    },
    [form, fetchLinkedOptions]
  );

  // Fetch lookup values and auto-populate inherited fields when a link field changes.
  //
  // `resolveLookups` (default true): whether to touch lookupValues at all.
  // Lookup fields are a snapshot captured once, at write time — resolving
  // them live is only correct when the user is DELIBERATELY changing the
  // link (handleFieldChange, the manual "Refresh lookups" button). It is
  // NOT correct on the automatic edit-mode load (useSubmitLinkLoader's
  // Effect 1): that fetch would overwrite the seeded captured value with
  // the linked record's CURRENT state moments after the form opens —
  // exactly the display bug this whole fix exists to close (2026-08-14
  // gate-review finding). Effect 1 still needs this function for its OTHER
  // job, resolving inherited-link display fields, hence the flag rather
  // than two separate functions.
  const fetchLookupAndInheritedValues = useCallback(
    async (
      linkFieldId: string,
      linkedRecordId: number | null,
      options?: { resolveLookups?: boolean },
    ) => {
      if (!form) return;
      const resolveLookups = options?.resolveLookups ?? true;

      // Find all lookup fields that depend on this link field
      const dependentLookups = resolveLookups
        ? form.fields.filter(
            (f) =>
              f.field_type?.code === "lookup" &&
              f.source_link_field_id === linkFieldId
          )
        : [];

      // Find all inherited fields that depend on this link field
      const inheritedFields = form.fields.filter(
        (f) => f.is_inherited && f.inherit_from_field_id === linkFieldId
      );

      if (dependentLookups.length === 0 && inheritedFields.length === 0) return;

      // If no record selected, clear values
      if (!linkedRecordId) {
        if (dependentLookups.length > 0) {
          const clearedLookups: Record<string, unknown> = {};
          for (const lookup of dependentLookups) {
            clearedLookups[lookup.id] = null;
          }
          setLookupValues((prev) => ({ ...prev, ...clearedLookups }));
        }
        for (const field of inheritedFields) {
          setFormData((prev) => ({ ...prev, [field.column_name]: "" }));
        }
        return;
      }

      // Find the link field to get the linked table ID
      const linkField = form.fields.find((f) => f.id === linkFieldId);
      if (!linkField?.linked_table_id) return;

      try {
        // Fetch the linked table's field definitions if not cached
        let linkedFields = linkedTableFields[linkField.linked_table_id];
        if (!linkedFields) {
          const linkedForm = await myappApi.tables.get(tokenRef.current, linkField.linked_table_id);
          linkedFields = linkedForm.fields;
          setLinkedTableFields((prev) => ({
            ...prev,
            [linkField.linked_table_id!]: linkedFields,
          }));
        }

        // Fetch the linked record
        const linkedRecord = await myappApi.records.get(
          tokenRef.current,
          linkField.linked_table_id,
          linkedRecordId
        );

        // Extract lookup values for each dependent lookup field
        const newLookupValues: Record<string, unknown> = {};
        for (const lookup of dependentLookups) {
          if (lookup.lookup_field_id) {
            const targetField = linkedFields.find(
              (f) => f.id === lookup.lookup_field_id
            );
            if (targetField) {
              let rawValue = linkedRecord[targetField.column_name];
              // If the target field is itself a link, the raw value is an FK integer.
              // Resolve it to a display name by fetching linked options.
              if (
                targetField.field_type?.code === "link" &&
                rawValue != null &&
                targetField.linked_table_id
              ) {
                try {
                  const opts = await myappApi.records.getLinkedOptions(
                    tokenRef.current,
                    formId,
                    targetField.linked_table_id,
                    { displayFieldId: targetField.linked_display_field_id || undefined }
                  );
                  const match = opts.find((o) => o.id === Number(rawValue));
                  if (match) {
                    rawValue = match.display_value;
                  } else {
                    // No matching option found - don't show raw FK
                    console.warn(`Lookup resolution: No option found for FK ${rawValue} in table ${targetField.linked_table_id}`);
                    rawValue = null;
                  }
                } catch (err) {
                  // API error - don't show raw FK value
                  console.error("Failed to resolve link field lookup:", err);
                  rawValue = null;
                }
              }
              newLookupValues[lookup.id] = rawValue;
            }
          }
        }
        // Guarded (not an unconditional spread): with resolveLookups false,
        // dependentLookups is empty so this object is always empty too —
        // skip the state update entirely rather than force a same-value
        // re-render (a new object identity would otherwise defeat React's
        // bail-out) of everything downstream of lookupValues.
        if (Object.keys(newLookupValues).length > 0) {
          setLookupValues((prev) => ({ ...prev, ...newLookupValues }));
        }

        // Auto-populate inherited field values
        // Match by linked_table_id: find the field in the parent table that
        // links to the same table as the inherited field
        for (const field of inheritedFields) {
          if (field.linked_table_id) {
            const sourceField = linkedFields.find(
              (f) => f.linked_table_id === field.linked_table_id
            );
            if (sourceField) {
              const inheritedValue = linkedRecord[sourceField.column_name];
              if (inheritedValue !== null && inheritedValue !== undefined) {
                setFormData((prev) => ({
                  ...prev,
                  [field.column_name]: inheritedValue,
                }));
                // Also store the display value from the _display column
                const displayCol = `${sourceField.column_name}_display`;
                const displayValue = linkedRecord[displayCol];
                if (displayValue) {
                  setInheritedDisplayValues((prev) => ({
                    ...prev,
                    [field.column_name]: String(displayValue),
                  }));
                }
              }
            }
          }
        }
      } catch (err) {
        console.error("Failed to fetch lookup/inherited values:", err);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [form, linkedTableFields]
  );

  // Manual "refresh lookups" — re-pull link dropdown OPTIONS (so an item added
  // to a linked table since the form opened appears) and re-resolve lookup +
  // inherited DISPLAY values for the current selections. It never changes the
  // selected FK values, so it's safe to fire at any time; handy when a lookup
  // shows blank (e.g. after a draft restore, which doesn't persist display
  // text) or a new linked record was just created elsewhere.
  const [refreshingLookups, setRefreshingLookups] = useState(false);
  const hasLookupRefreshTargets = useMemo(
    () =>
      (form?.fields ?? []).some(
        (f) => f.field_type?.code === "link" || f.field_type?.code === "lookup",
      ),
    [form?.fields],
  );
  const handleRefreshLookups = useCallback(async () => {
    if (!form) return;
    setRefreshingLookups(true);
    try {
      const linkFields = form.fields.filter(
        (f) => f.field_type?.code === "link" && f.linked_table_id,
      );
      // 1. Refresh dropdown options. Cascading children use their parent's
      //    current value (mirrors fetchData) so their filtered list stays right.
      await Promise.all(
        linkFields.map((field) => {
          if (!field.cascade_parent_field_id) return fetchLinkedOptions(field);
          const parentField = form.fields.find(
            (f) => f.id === field.cascade_parent_field_id,
          );
          const parentValue = parentField
            ? formData[parentField.column_name]
            : null;
          if (parentValue && field.cascade_filter_column) {
            return fetchLinkedOptions(
              field,
              field.cascade_filter_column,
              Number(parentValue),
            );
          }
          return Promise.resolve();
        }),
      );
      // 2. Re-resolve lookup + inherited display values for current selections.
      await Promise.all(
        linkFields.map((field) => {
          const selected = formData[field.column_name];
          const recordId =
            selected != null && selected !== "" ? Number(selected) : null;
          return fetchLookupAndInheritedValues(field.id, recordId);
        }),
      );
      // Neutral copy: the inner fetchers swallow their own errors (logging to
      // console), so we can't assert every lookup actually resolved — avoid a
      // false "success" claim on a button whose job is fixing blanks.
      toast.success("Lookups updated");
    } finally {
      setRefreshingLookups(false);
    }
  }, [form, formData, fetchLinkedOptions, fetchLookupAndInheritedValues]);

  // Evaluate a concatenation template using current form data and lookup values
  const evaluateConcatenation = useCallback(
    (template: string | null | undefined): string | null => {
      if (!form) return null;
      return evaluateConcatenationFn(template, form.fields, formData, lookupValues, linkDisplayValues, linkedOptions);
    },
    [form, formData, lookupValues, linkDisplayValues, linkedOptions]
  );

  // Calc-on-calc: evaluate every calculation field once, in dependency order,
  // so a calculation that references another calculation resolves correctly
  // (cycle members → null). The backend (apply_calculations_to_row) is
  // authoritative on write; this map drives both the live preview and the
  // submit payload. Keyed by column_name.
  const calcValuesResult = useMemo(
    () => evaluateAllCalculations(form?.fields ?? [], formData, lookupValues),
    [form?.fields, formData, lookupValues],
  );
  const calcValueFor = useCallback(
    (field: FormField): string | number | null =>
      calcValuesResult.values[field.column_name] ?? null,
    [calcValuesResult],
  );

  // v2 (T-SQL) calc field values map — same alias-building as the legacy
  // evaluator's values (display name / underscore / no-space / column name,
  // plus lookup-field fallback) so a v2 IR resolves a sibling exactly the
  // way v1's evaluateAllCalculations already does.
  const calcIrValues = useMemo(
    () => buildFieldValueMap(form?.fields ?? [], formData, lookupValues),
    [form?.fields, formData, lookupValues],
  );
  // Dispatches on spec version. v2 NEVER falls back to the legacy evaluator
  // — computed-on-save and a malformed/absent ir are documented success
  // states of evaluateCalculatedField, not reasons to guess with v1 logic.
  //
  // Most real v2 formulas (CASE, DATEPART, string functions, ...) have no
  // browser IR mirror at all (see tsql-ir.ts), so the live evaluator resolves
  // to "computed on save" for them regardless of the record's actual state.
  // That's the correct DESIGN-TIME placeholder, but wrong once a record
  // already holds a real, SQL-Server-computed value — an existing record's
  // fetched `formData` is always authoritative here, so prefer it whenever
  // the live evaluator has nothing to mirror. A brand-new/unsaved record has
  // no stored value yet, so it still falls through to the placeholder.
  const calcDisplayFor = useCallback(
    (field: FormField): CalculatedFieldDisplay => {
      if (field.calculation_spec_version !== 2) {
        return { calculatedValue: calcValueFor(field), calculationComputedOnSave: false };
      }
      const live = evaluateCalculatedField(field.calculation_ir, calcIrValues);
      if (live.calculationComputedOnSave) {
        const stored = formData[field.column_name];
        if (stored !== undefined && stored !== null) {
          return { calculatedValue: stored as number | string, calculationComputedOnSave: false };
        }
      }
      return live;
    },
    [calcIrValues, calcValueFor, formData],
  );

  // True when the page opens to view an existing record (no ?mode=edit, or
  // any virtual record — those never leave view mode).
  // View mode renders link/inherited values from the row's denormalised
  // display columns, so dropdown options and inherited resolves are skipped
  // until the user clicks Edit.
  const startInView = isEditMode && (mode !== "edit" || isVirtual);

  // `overrideId`, when passed, lets a caller fetch a SPECIFIC record right
  // after creating it, instead of relying on `editId` (read from the URL's
  // search params) to update first. Needed because right after `create()`
  // resolves, the URL hasn't been navigated yet — `editId`/`isEditMode` here
  // are still the pre-create ("new record") values for the rest of this
  // render, so a plain `fetchData()` call at that point would run the
  // new-record-defaults branch, not fetch the just-created row.
  const fetchData = useCallback(async (overrideId?: number) => {
    const myFetchGeneration = ++fetchGenerationRef.current;
    const effectiveEditId = overrideId ?? editId;
    const effectiveIsEditMode = overrideId !== undefined || isEditMode;
    // First call → replace the page with "Loading table…". Subsequent
    // refetches (after save / transition) → keep the record visible
    // and toggle the `refreshing` indicator instead.
    const isInitial = isInitialFetchRef.current;
    // Form metadata comes from `useTableMetadata` (Phase B cache). If
    // it's still loading on the first mount, bail out — this effect
    // will re-run once `form` resolves because it's a dependency. On
    // subsequent calls (post-save/transition refetch) the cache is
    // warm and `form` is already set.
    if (!form) {
      if (isInitial && metadataError) {
        setError(metadataError);
        setLoading(false);
        isInitialFetchRef.current = false;
      }
      return;
    }
    const formDetail = form;

    // Advanced (header) tables render <HeaderLineForm/> below, which loads
    // the composite record (header + lines) itself. submit.tsx's record +
    // link-option fetch here is entirely unused for them, so skip it —
    // otherwise we pay a duplicate header round-trip (against remote Azure
    // SQL) on the critical path before HeaderLineForm even mounts.
    if (formDetail.is_header_table) {
      if (isInitial) {
        setLoading(false);
        isInitialFetchRef.current = false;
      }
      return;
    }

    try {
      if (isInitial) setLoading(true);
      else setRefreshing(true);

      // If editing (or fetching a just-created record via overrideId), fetch
      // the existing record
      if (effectiveIsEditMode && effectiveEditId) {
        const record = await myappApi.records.get(tokenRef.current, formId, Number(effectiveEditId));
        // A newer fetchData call started while this await was in flight —
        // discard this response rather than apply stale data on top of (or
        // racing) whatever the newer call is about to set.
        if (fetchGenerationRef.current !== myFetchGeneration) return;

        // Capture state-machine context if present (record._state).
        // Validate the shape — an empty {} or partial _state shouldn't
        // pass as a RecordStateContext and break the renderer.
        const rawState = (record as Record<string, unknown> | null | undefined)?._state as
          | Partial<import("@/types/myapp").RecordStateContext>
          | undefined;
        if (
          rawState &&
          rawState.current_state &&
          typeof rawState.current_state === "object" &&
          "id" in rawState.current_state &&
          "name" in rawState.current_state
        ) {
          setRecordState(rawState as import("@/types/myapp").RecordStateContext);
        } else {
          setRecordState(null);
        }

        // hydrateRowToFormData encapsulates the {col}_fk binding rule for
        // link fields. See edit-hydration.ts for the full contract.
        const hydrated = hydrateRowToFormData(formDetail.fields, record);
        setFormData(hydrated.data);
        const rawVersion = (record as Record<string, unknown> | null)?.[VERSION_TOKEN_KEY];
        recordVersionRef.current =
          typeof rawVersion === "string" && rawVersion ? rawVersion : null;
        // 🔴 BUMP THE EPOCH ON THE LOAD TOO. This is the record-identity change
        // the timestamp guard could not see: any reconcile read still in flight
        // against the PREVIOUS record is now stale by definition, whatever its
        // timestamp says relative to this one.
        versionEpochRef.current += 1;
        loadedDataRef.current = { ...hydrated.data };
        // Marks which editId this formData snapshot actually belongs to —
        // read by the lookupValues seed effect below. The PK column isn't
        // in formDetail.fields (hydrateRowToFormData never sets it), so it
        // can't be recovered from formData after the fact; this is set at
        // the one point that genuinely knows the correspondence.
        //
        // effectiveEditId, NOT the closed-over `editId` — this function
        // also runs as fetchData(newRecordId) right after a create, where
        // effectiveEditId is the new record but the URL (and therefore
        // `editId`) hasn't caught up yet (the `navigate({ search: { edit:
        // newRecordId } })` a few lines below this callback hasn't fired).
        // Stamping the stale `editId` here (undefined, mid-create) left the
        // seed effect's guard permanently unsatisfiable until the
        // URL-driven refetch completed — reintroducing, transiently, the
        // exact "just-created record shows blank lookups" bug this whole
        // seed exists to close (2026-08-14 gate-review finding).
        hydratedForEditIdRef.current = effectiveEditId;
        if (Object.keys(hydrated.inheritedDisplay).length > 0) {
          setInheritedDisplayValues(hydrated.inheritedDisplay);
        }
        if (Object.keys(hydrated.linkDisplay).length > 0) {
          setLinkDisplayValues(hydrated.linkDisplay);
        }
        if (Object.keys(hydrated.linkSnapshot).length > 0) {
          setLinkSnapshotValues(hydrated.linkSnapshot);
        }
      } else {
        // Initialize form data with defaults for new record
        const initialData: Record<string, unknown> = {};
        formDetail.fields.forEach((field) => {
          const defaultValue = field.default_value;
          const fieldType = field.field_type?.code;

          // Check if default is a SQL function (should be handled by database, not pre-populated)
          const isSqlFunction = defaultValue && (
            defaultValue.toUpperCase() === "GETDATE()" ||
            defaultValue.toUpperCase().startsWith("DATEADD(") ||
            defaultValue.toUpperCase() === "NEWID()"
          );

          if (fieldType === "bit") {
            initialData[field.column_name] =
              defaultValue === "1" || defaultValue === "true";
          } else if (isSqlFunction) {
            // SQL function defaults - calculate on client for UX, will be overridden by DB if empty
            if (defaultValue?.toUpperCase() === "GETDATE()") {
              // Today's date in YYYY-MM-DD format
              initialData[field.column_name] = new Date().toISOString().split("T")[0];
            } else if (defaultValue?.toUpperCase().startsWith("DATEADD(")) {
              // Parse DATEADD(DAY,X,GETDATE()) and calculate the date
              const match = defaultValue.match(/DATEADD\(DAY,(-?\d+),GETDATE\(\)\)/i);
              if (match) {
                const days = parseInt(match[1]);
                const date = new Date();
                date.setDate(date.getDate() + days);
                initialData[field.column_name] = date.toISOString().split("T")[0];
              } else {
                initialData[field.column_name] = "";
              }
            } else {
              initialData[field.column_name] = "";
            }
          } else {
            // Use null check instead of falsy check to preserve "0" defaults
            initialData[field.column_name] = defaultValue !== null && defaultValue !== undefined ? defaultValue : "";
          }
        });
        // No await above this point in the branch, so generation can't have
        // moved — still checked for symmetry/future-proofing if that changes.
        if (fetchGenerationRef.current !== myFetchGeneration) return;
        setFormData(initialData);
        // Stamp symmetrically with the edit branch above. Without this, a
        // Back navigation (edit -> create-defaults, same mount) leaves the
        // ref pointing at the PREVIOUS record while formData is now blank
        // defaults; a subsequent Forward navigation back to that same
        // editId then sees hydratedForEditIdRef already "matching" and
        // skips re-clearing/re-seeding — permanently seeding the lookup
        // state against blank defaults instead of the real record
        // (2026-08-14 gate-review finding).
        hydratedForEditIdRef.current = effectiveEditId;
      }

      // Fetch linked options for link fields (skip in view mode — dropdowns
      // are not rendered, and link display values come from the row's
      // _display columns hydrated above).
      // Skip cascading fields - they'll be fetched when parent is selected
      if (!startInView) {
        formDetail.fields.forEach((field) => {
          if (field.field_type?.code === "link" && field.linked_table_id) {
            if (field.cascade_parent_field_id) {
              // This is a cascading field - check if parent has a value
              const parentField = formDetail.fields.find(
                (f) => f.id === field.cascade_parent_field_id
              );
              if (parentField) {
                const parentValue = isEditMode && editId
                  ? formData[parentField.column_name]
                  : null;
                if (parentValue && field.cascade_filter_column) {
                  fetchLinkedOptions(
                    field,
                    field.cascade_filter_column,
                    Number(parentValue)
                  );
                }
                // Otherwise leave empty until parent is selected
              }
            } else {
              // Regular link field - fetch all options
              fetchLinkedOptions(field);
            }
          }
        });
      }

      if (fetchGenerationRef.current === myFetchGeneration) setError(null);
    } catch (err) {
      // Same race as the success-path checks above, on the error arm: an
      // older, now-superseded call's rejection must not paint a stale
      // error banner over a newer call's (possibly successful) result.
      if (fetchGenerationRef.current === myFetchGeneration) {
        setError(err instanceof Error ? err.message : "Failed to load table");
      }
    } finally {
      // And on loading/refreshing indicators — an older call's finally
      // must not drop a newer call's still-in-flight indicator, nor
      // prematurely flip isInitialFetchRef while the real first fetch
      // (whichever generation turns out not to be superseded) is still
      // running.
      if (fetchGenerationRef.current === myFetchGeneration) {
        if (isInitial) {
          setLoading(false);
          isInitialFetchRef.current = false;
        } else {
          setRefreshing(false);
        }
      }
    }
    // `token` is intentionally absent: it is read via `tokenRef.current` so a
    // silent JWT refresh doesn't recreate this callback and wipe an in-progress
    // new record (see tokenRef declaration above).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formId, isEditMode, editId, fetchLinkedOptions, form, metadataError]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Seed lookupValues from formData on edit/view load. Lookup columns are
  // always in formData (the record payload), but the render path (view
  // mode and edit mode alike — both have isEditMode true, see isViewing
  // above) reads from lookupValues state. Without this seed, every lookup
  // falls through to the "Auto-filled from linked record" placeholder / a
  // bare dash. Mirrors HeaderLineForm.tsx's identical seed.
  //
  // Two effects, not one — a single one-shot ref (as first shipped, and as
  // HeaderLineForm.tsx still has) has two bugs a 2026-08-14 gate-review
  // caught:
  //   1. It fires on ANY settle, including a brand-new record's empty
  //      form — burning the ref before there's anything to seed, so a
  //      just-created record's post-save view never gets seeded.
  //   2. TanStack Router can reuse this component across `?edit=` changes
  //      (record A -> record B) without unmounting. `loading` never
  //      toggles for that navigation (only `refreshing` does — see
  //      `isInitialFetchRef`), so the ref-guarded effect never re-fires,
  //      and record B renders record A's stale lookupValues.
  //
  // Fix: gate on `isEditMode` (excludes create-mode, closing #1), and key
  // re-seeding on `editId` actually matching `hydratedForEditIdRef` — the
  // editId fetchData most recently hydrated formData for (closes #2 —
  // waits out the render where formData still holds the previous record
  // while `editId` has already moved on).
  const seededForEditIdRef = useRef<number | undefined>(undefined);
  const prevEditIdRef = useRef(editId);
  useEffect(() => {
    // Record identity changed (a same-mount A -> B navigation): drop the
    // previous record's lookup display state immediately, and allow the
    // seed effect below to re-arm for the new record. Must not merge
    // record B's seed on top of record A's leftover values.
    if (prevEditIdRef.current !== editId) {
      prevEditIdRef.current = editId;
      // Skip the clear when formData was already hydrated for THIS editId
      // ahead of the URL catching up — the post-create prefetch
      // (fetchData(newRecordId), see hydratedForEditIdRef above) can seed
      // lookupValues correctly before `navigate({ search: { edit: ... } })`
      // updates `editId` to match. Clearing here would immediately wipe
      // out that already-correct seed for no reason; it's not a record
      // switch, just the URL catching up to data that's already right.
      if (hydratedForEditIdRef.current !== editId) {
        seededForEditIdRef.current = undefined;
        setLookupValues({});
      }
    }
  }, [editId]);
  useEffect(() => {
    if (!form || loading || !isEditMode) return;
    // formData hasn't caught up to `editId` yet (mid-refetch after a
    // record-to-record navigation) — wait for the render where it has.
    // hydratedForEditIdRef is set at the exact point fetchData calls
    // setFormData, so this is a direct correspondence check, not a guess
    // from formData's contents (the PK column isn't in formDetail.fields,
    // so it never lands in formData at all — an earlier version of this
    // guard tried to compare it and could therefore never fire).
    if (hydratedForEditIdRef.current !== editId) return;
    if (seededForEditIdRef.current === editId) return;
    seededForEditIdRef.current = editId;

    const initialLookupValues: Record<string, unknown> = {};
    for (const field of form.fields) {
      if (field.field_type?.code === "lookup") {
        const val = formData[field.column_name];
        if (val !== null && val !== undefined && val !== "") {
          initialLookupValues[field.id] = val;
        }
      }
    }
    if (Object.keys(initialLookupValues).length > 0) {
      setLookupValues((prev) => ({ ...initialLookupValues, ...prev }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, loading, isEditMode, editId, formData]);

  // Pre-emptively refresh the session every 10 minutes while a capture form is
  // open. Keeps the 30-min access token alive during a long capture (the
  // "active for an hour" case) and avoids the 401 → reactive-refresh stutter on
  // save. This is a complement to the tokenRef change above, not a substitute:
  // it still rotates `token`, so tokenRef is what actually prevents the wipe.
  // Mirrors ReportBuilderWizard's 10-min refresh interval.
  useEffect(() => {
    const id = setInterval(() => void refreshSession(), 10 * 60 * 1000);
    return () => clearInterval(id);
  }, [refreshSession]);

  // Workflow states (tableStates) are sourced from `useTableMetadata`
  // above. The Phase A standalone `tableStates.list` effect was
  // dropped because the cached hook already serves the same data with
  // a 5-minute staleTime, removing one round trip per submit-page mount.

  useSubmitLinkLoader({
    form,
    formData,
    isEditMode,
    isViewing,
    startInView,
    loading,
    fetchLinkedOptions,
    fetchLookupAndInheritedValues,
  });

  const handleFieldChange = (columnName: string, value: unknown) => {
    setFormData((prev) => ({ ...prev, [columnName]: value }));
    if (fieldErrors[columnName]) {
      setFieldErrors((prev) => { const next = { ...prev }; delete next[columnName]; return next; });
    }

    // Decimal precision entry guard — re-validate on every keystroke so the
    // field shows red + the message immediately, the same moment grid.tsx's
    // NumericCell would refuse to commit the equivalent cell edit.
    const changedFieldForPrecision = form?.fields.find((f) => f.column_name === columnName);
    if (changedFieldForPrecision) {
      const precisionError = decimalPrecisionError(changedFieldForPrecision, value);
      if (precisionError) {
        setFieldErrors((prev) => ({ ...prev, [columnName]: precisionError }));
      }
    }

    if (form) {
      const changedField = form.fields.find((f) => f.column_name === columnName);
      if (changedField && changedField.field_type?.code === "link") {
        // Capture display value for link fields
        if (value !== null && value !== undefined && value !== "") {
          const options = linkedOptions[changedField.id] || [];
          const selectedOption = options.find((opt) => opt.id === Number(value));
          if (import.meta.env.DEV) {
            console.log(`[FIELD_CHANGE] Link field "${changedField.name}" (${columnName}):`, {
              value,
              optionsCount: options.length,
              selectedOption,
            });
          }
          if (selectedOption) {
            // Always store a display value - fall back to ID if display_value is empty
            const displayToStore = selectedOption.display_value || String(selectedOption.id);
            if (import.meta.env.DEV) {
              console.log(`[FIELD_CHANGE] Storing display value: "${displayToStore}"`);
            }
            setLinkDisplayValues((prev) => ({
              ...prev,
              [columnName]: displayToStore,
            }));
          } else if (import.meta.env.DEV) {
            console.warn(`[FIELD_CHANGE] No selectedOption found for value ${value} in options:`, options);
          }
        } else {
          // Clear display value when link is cleared
          setLinkDisplayValues((prev) => {
            const updated = { ...prev };
            delete updated[columnName];
            return updated;
          });
        }

        // Fetch lookup and inherited values for dependent fields
        fetchLookupAndInheritedValues(
          changedField.id,
          value ? Number(value) : null
        );

        // Check if any field cascades from this one
        const hasCascadingChildren = form.fields.some(
          (f) => f.cascade_parent_field_id === changedField.id
        );
        if (hasCascadingChildren) {
          refreshCascadingOptions(
            changedField.id,
            value ? Number(value) : null
          );

          // Clear non-inherited child field values when parent changes
          // (inherited fields are auto-populated by fetchLookupAndInheritedValues)
          form.fields
            .filter(
              (f) =>
                f.cascade_parent_field_id === changedField.id &&
                !f.is_inherited
            )
            .forEach((childField) => {
              setFormData((prev) => ({ ...prev, [childField.column_name]: null }));
              // Also clear the display value
              setLinkDisplayValues((prev) => {
                const updated = { ...prev };
                delete updated[childField.column_name];
                return updated;
              });
            });
        }
      }
    }
  };

  const handleUniqueFieldBlur = useCallback(async (field: FormField) => {
    if (!field.is_unique) return;
    const value = formData[field.column_name];
    const col = field.column_name;
    if (value === null || value === undefined || value === "") {
      setFieldErrors((prev) => { const next = { ...prev }; delete next[col]; return next; });
      return;
    }
    try {
      const result = await myappApi.records.validateRow(
        token,
        formId,
        { [col]: value },
        isEditMode && editId ? Number(editId) : undefined,
      );
      if (result.errors.length > 0) {
        setFieldErrors((prev) => ({ ...prev, [col]: result.errors[0] }));
      } else {
        setFieldErrors((prev) => { const next = { ...prev }; delete next[col]; return next; });
      }
    } catch {
      // Silently ignore network errors during blur validation
    }
  }, [token, formId, formData, isEditMode, editId]);

  const handleSubmit = async (
    opts: { closeAfter?: boolean; skipRefetch?: boolean } = { closeAfter: true },
  ) => {
    if (!form) return false;
    const closeAfter = opts.closeAfter ?? true;
    // skipRefetch: when the caller (e.g. the transition wrapper) will do
    // its own refetch right after, don't refetch here too — collapses
    // the back-to-back refreshes that caused the screen to flash twice.
    const skipRefetch = opts.skipRefetch ?? false;

    // Pre-submit attachment check. The backend stores an INT count column
    // that defaults to 0, so a required attachment with zero files would
    // happily save and the user would only notice the file isn't there
    // when they reopen the record. Bail out early with a clear toast.
    // Pure helper is unit-tested in required-attachment-misses.test.ts.
    // We strip the `where` prefix for the simple-form toast — the user is
    // looking at a single record, so naming the form is just noise.
    const attachmentMisses = findRequiredAttachmentMissesForRecord({
      form,
      formData,
      isEditMode,
      isFieldLocked: (id) => lockedFieldSet.has(id),
      pendingFilesFor: (id) =>
        pendingAttachmentsRef.current.get(id)?.current.length ?? 0,
    }).map((m) => ({ ...m, where: "" }));
    if (attachmentMisses.length > 0) {
      toast.error(
        `Attach a file before saving: ${formatAttachmentMisses(attachmentMisses)}.`,
      );
      return false;
    }

    // Pre-submit decimal precision check — a hard gate alongside the live
    // per-keystroke validation in handleFieldChange, so a value that slipped
    // through (e.g. restored from a draft) can never reach the backend.
    // Mirrors the grid's "nothing is written" rejection: not a silent round.
    const precisionMisses: { field: FormField; message: string }[] = [];
    for (const field of form.fields) {
      const message = decimalPrecisionError(field, formData[field.column_name]);
      if (message) precisionMisses.push({ field, message });
    }
    if (precisionMisses.length > 0) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        for (const { field, message } of precisionMisses) next[field.column_name] = message;
        return next;
      });
      toast.error(
        `Fix before saving: ${precisionMisses.map((m) => `${m.field.name} — ${m.message}`).join("; ")}.`,
      );
      return false;
    }

    try {
      setSaving(true);

      // Convert form data to the correct types.
      //
      // The walk is a closure over its data SOURCE so it can be run twice:
      // once over the live `formData`, once over `loadedDataRef.current` —
      // the hydrated snapshot this record was read with. Diffing the two
      // payloads on an edit is what stops an untouched column being re-sent,
      // so a form that loaded before a workflow wrote to the row no longer
      // writes its stale values back over it (see submit-diff.ts).
      //
      // Everything else the walk reads — `lookupValues`, `linkDisplayValues`,
      // `inheritedDisplayValues`, `calcValueFor`, the locked-field set — is
      // deliberately NOT parameterised: those are live component state with no
      // per-load copy, so both payloads see the same values and the columns
      // they feed fall out of the diff. That is correct, not a gap: the
      // backend is the sole writer of lookup and calculation columns and
      // discards any client-sent value for them on update (lara-data-plane
      // `DataService._apply_calculation_values`, and the lookup strip in
      // `update_record` / the composite update prep).
      const buildRecordPayload = (
        source: Record<string, unknown>,
      ): Record<string, unknown> => {
        const submitData: Record<string, unknown> = {};
        form.fields.forEach((field) => {
          // Skip state-locked fields on update — the backend would reject
          // the whole save if it sees them in the payload. Locked fields
          // are read-only in the UI anyway, so the value isn't changing.
          // (On create, no state machine is active yet and the lockdown
          // set is empty.)
          if (isEditMode && lockedFieldSet.has(field.id)) return;

          // Shared-FK collision: collided link fields are built into the payload
          // normally here (display snapshot + per-field SysFk via fkWriteKey).
          // After this loop, on an EDIT, stripUntouchedCollisionFields removes any
          // collided field the user did NOT actively re-select — leaving its stored
          // snapshot + per-field SysFk untouched. Only a touched field persists;
          // the stored snapshot value is gold and is never machine-altered.

          const fieldType = field.field_type?.code;

          // Computed fields (calculation/concatenation) are always included,
          // even if hidden, since they have physical columns in the database.
          // Calculation fields: compute value from formula and include it
          if (fieldType === "calculation") {
            // v2 (T-SQL) calc columns are OMITTED entirely, never sent as
            // null — SQL Server is the ONLY thing that computes a v2 stored
            // value, inside the same statement that writes the row. Sending
            // even a correct-looking client value would make the browser a
            // second writer, exactly the parity risk this migration removes.
            if (field.calculation_spec_version === 2) {
              return;
            }
            // v1: dependency-ordered value (supports calc-on-calc). The
            // backend recomputes authoritatively, but sending the correct
            // value keeps optimistic UI and any non-recomputing path
            // consistent.
            const calcVal = calcValueFor(field);
            if (calcVal !== null && !isNaN(Number(calcVal))) {
              submitData[field.column_name] = Number(calcVal);
            }
            return;
          }

          // Concatenation fields are computed server-side from the row's
          // source-field values via DataService._apply_concatenation_values.
          // The frontend evaluator stays alive as a live preview while the
          // user types, but never contributes to the submit payload - that
          // path used to be the source of the "1_6_1_1" corruption when
          // linkDisplayValues hadn't been populated by the time submit fired.
          if (fieldType === "concatenation") {
            return;
          }

          // Lookup fields: include the resolved value as a snapshot
          if (fieldType === "lookup") {
            const lookupVal = lookupValues[field.id];
            if (lookupVal !== null && lookupVal !== undefined) {
              submitData[field.column_name] = lookupVal;
            }
            return;
          }

          // Link fields: store display value + FK in separate columns
          // User column gets display text, system FK column gets the ID
          if (fieldType === "link") {
            const fkValue = source[field.column_name];
            if (fkValue !== null && fkValue !== undefined && fkValue !== "") {
              // Get display value from appropriate source
              // - Regular link fields: linkDisplayValues
              // - Inherited link fields: inheritedDisplayValues
              const displayVal = field.is_inherited
                ? inheritedDisplayValues[field.column_name]
                : linkDisplayValues[field.column_name];
              // Always include display value - fall back to FK ID if display text is missing
              // (This prevents NOT NULL violations when the column is required)
              submitData[field.column_name] = displayVal || String(fkValue);
              // Write the FK under the field's per-field canonical column when on
              // the per-field scheme, else the legacy shared key. Collided links
              // MUST use their own key (see fkWriteKey).
              const fkKey = fkWriteKey(field);
              if (fkKey) {
                submitData[fkKey] = Number(fkValue);
              }
            }
            return;
          }

          // Skip hidden and auto-numbered fields for regular data
          if (field.is_hidden) return;
          if (field.is_auto_numbered && !isEditMode) return;

          // Signature and attachment fields store an INT count populated by the
          // backend after blob link. On create the client sends 0 so NOT NULL
          // columns don't reject the insert; the real count is written by
          // _sync_attachment_count during the post-create pending-blob flush.
          if (fieldType === "signature" || fieldType === "attachment") {
            if (!isEditMode) submitData[field.column_name] = 0;
            return;
          }

          const value = source[field.column_name];

          // Handle empty values
          if (value === "" || value === null || value === undefined) {
            if (!field.is_required) {
              submitData[field.column_name] = null;
            }
            return;
          }

          // Convert to appropriate types
          switch (fieldType) {
            case "int":
            case "bigint":
              submitData[field.column_name] = Number(value);
              break;
            case "decimal":
            case "money":
              submitData[field.column_name] = parseFloat(String(value));
              break;
            case "bit":
              submitData[field.column_name] = Boolean(value);
              break;
            default:
              submitData[field.column_name] = value;
          }
        });
        return submitData;
      };

      // The payload as the form currently stands, and — on an edit — only the
      // part of it that actually moved since the record was loaded. A key
      // absent from the diff means "do not write this column"; it is never
      // sent as an explicit null.
      const submitData = isEditMode
        ? diffSubmitData(
            buildRecordPayload(formData),
            buildRecordPayload(loadedDataRef.current ?? {}),
            form.fields,
          )
        : buildRecordPayload(formData);

      // Persist-only-on-user-selection: on an EDIT, drop every collided link
      // field the user did NOT actively re-select, so its stored snapshot + its
      // per-field SysFk stay in the DB exactly as they are (an unresolved field's
      // SysFk simply stays NULL until the user picks a value). The stored
      // snapshot is gold and is never machine-altered. New records keep all
      // their genuinely user-entered values. See shared-fk-collision.ts.
      const finalData = isEditMode
        ? stripUntouchedCollisionFields(
            submitData,
            form.fields,
            sharedFkCollisionCols,
            touchedCollisionCols,
          )
        : submitData;

      if (import.meta.env.DEV) {
        console.log("[SUBMIT] Final submitData:", JSON.stringify(finalData, null, 2));
      }

      if (isEditMode && editId) {
        // If every editable field got stripped (record is fully locked, or
        // user changed nothing), skip the API call — the backend would
        // reject an empty payload with "No valid data provided". Closing
        // is the right outcome; "stay" is a no-op.
        if (Object.keys(finalData).length === 0) {
          if (closeAfter) {
            navigate({ to: "/portal/myapp/$tableId", params: { tableId: String(formId) } });
          }
          return true;
        }
        try {
          // 🔴 THE RESPONSE IS THE NEW TOKEN — DO NOT DISCARD IT. This
          // endpoint answers with the row a GET would return (see
          // `adoptVersionToken`), and the reviewed advance calls this with
          // `skipRefetch: true` on purpose, so nothing else re-reads the
          // record before the review opens. Adopting here is what makes the
          // ref fresh SYNCHRONOUSLY, inside `saving`, rather than at some
          // point during a later fire-and-forget GET the form stayed
          // editable across.
          const updated = await myappApi.records.update(
            token,
            formId,
            Number(editId),
            finalData,
            recordVersionRef.current,
          );
          adoptVersionToken(updated);
        } catch (err) {
          const conflict = asRecordChanged(err);
          if (!conflict) throw err;
          // Somebody else saved first. Refresh only the fields this user
          // has NOT touched, keep what they typed, adopt the new version
          // token so a deliberate second save goes through, and tell them
          // who moved it. No merge UI: guessing a merge on a price is the
          // failure this module exists to remove.
          if (conflict.current) {
            // Through `mergeConflictRowIntoFormData`, never by assigning
            // raw API columns: a link field's form value is its FK id,
            // not the display snapshot in `row[col]`. See that helper for
            // what splicing the raw row destroyed.
            // Computed OUTSIDE the state updater: an updater that also
            // writes a ref runs twice under StrictMode. `formData` is the
            // same value this submit built its payload from a few lines
            // above, so there is nothing staler about reading it here.
            const merged = mergeConflictRowIntoFormData(
              form.fields,
              conflict.current,
              formData,
              loadedDataRef.current,
            );
            setFormData(merged.data);
            loadedDataRef.current = merged.loaded;
            const mergedLinkDisplay = merged.linkDisplay;
            // The link display text is separate state from the form
            // value; without this the combobox keeps rendering the label
            // it was loaded with while its bound FK has moved on.
            if (Object.keys(mergedLinkDisplay).length > 0) {
              setLinkDisplayValues((prev) => ({ ...prev, ...mergedLinkDisplay }));
            }
          }
          if (conflict.changedAtUtc) {
            // Authoritative, same as a write response: the server named the
            // token it currently holds. Bump so an older read in flight cannot
            // undo it.
            recordVersionRef.current = conflict.changedAtUtc;
            versionEpochRef.current += 1;
          }
          showRecordConflictToast(conflict);
          return false;
        }
        // Re-baseline on the successful write itself, BEFORE the refetch
        // below — which is skipped on the save-and-transition path and can
        // fail on its own. Leaving the snapshot describing the pre-save row
        // makes a later revert to the original value diff to empty: the
        // record keeps the superseded value, the form shows the reverted
        // one, and `:1431` returns success without a request. Sending the
        // whole payload used to mask this by producing a 409 — that applies
        // here, where a version token rides the update; the composite path
        // has no token (plan D8) and was masked by the wholesale overwrite.
        loadedDataRef.current = { ...formData };
        clearDraft();
        toast.success("Record updated successfully");
        if (closeAfter) {
          navigate({ to: "/portal/myapp/$tableId", params: { tableId: String(formId) } });
        } else if (!skipRefetch) {
          // Stay on page — refetch so any backend-side derived values
          // (rollups, calc, state context) refresh in the form. Skip
          // when the caller will refetch immediately afterwards
          // (e.g. the transition wrapper).
          await fetchData();
        }
        return true;
      } else {
        const result = await myappApi.records.create(token, formId, submitData);
        // Record is persisted — drop the local draft (blob uploads below may
        // still fail independently, but the record itself is saved).
        clearDraft();

        // Upload any pending signature blobs now that we have a record ID
        const newRecordId = result.id ?? (result as Record<string, unknown>)[form.primary_key_column];
        if (newRecordId) {
          for (const [sigFieldId, blobRef] of pendingSignaturesRef.current.entries()) {
            if (blobRef.current) {
              try {
                const sigField = form.fields.find(f => f.id === sigFieldId);
                const filename = `signature_${sigField?.column_name ?? sigFieldId}.png`;

                // Initiate upload
                const initResult = await myappApi.attachments.initiateUpload(
                  token,
                  formId,
                  Number(newRecordId),
                  { filename, file_size_bytes: blobRef.current.size, field_id: sigFieldId },
                );

                // PUT blob to presigned URL. 2-minute timeout + .ok check
                // prevents a stalled or CORS-blocked storage upload from
                // wedging the parent's "Saving..." state forever. See the
                // matching safety rails in HeaderLineForm.uploadSignatureBlob.
                const uploadResp = await fetch(initResult.presigned_url, {
                  method: "PUT",
                  headers: { "Content-Type": "image/png", "x-ms-blob-type": "BlockBlob" },
                  body: blobRef.current,
                  signal: AbortSignal.timeout(120_000),
                });
                if (!uploadResp.ok) {
                  throw new Error(
                    `Signature upload failed: ${uploadResp.status} ${uploadResp.statusText}`,
                  );
                }

                // Complete upload
                await myappApi.attachments.completeUpload(
                  token,
                  formId,
                  Number(newRecordId),
                  { asset_id: initResult.asset_id },
                );

                // Link attachment to the field
                await myappApi.attachments.linkAttachment(
                  token,
                  formId,
                  Number(newRecordId),
                  {
                    asset_id: initResult.asset_id,
                    field_id: sigFieldId,
                    filename,
                    file_size_bytes: blobRef.current.size,
                    mime_type: "image/png",
                    replace_existing: true,
                  },
                );

                blobRef.current = null;
              } catch (err) {
                console.error(`Failed to upload signature for field ${sigFieldId}:`, err);
                toast.error("Signature upload failed, but record was saved");
              }
            }
          }

          // Upload any pending attachment files (multi-file per field, stackable)
          let attachmentFailures = 0;
          for (const [attFieldId, filesRef] of pendingAttachmentsRef.current.entries()) {
            for (const file of filesRef.current) {
              try {
                const initResult = await myappApi.attachments.initiateUpload(
                  token,
                  formId,
                  Number(newRecordId),
                  { filename: file.name, file_size_bytes: file.size, field_id: attFieldId },
                );

                // Same 2-minute timeout + .ok check as the signature path
                // above. 120s is comfortably above realistic upload time
                // for the platform's per-file size limit.
                const uploadResp = await fetch(initResult.presigned_url, {
                  method: "PUT",
                  headers: { "Content-Type": file.type || "application/octet-stream", "x-ms-blob-type": "BlockBlob" },
                  body: file,
                  signal: AbortSignal.timeout(120_000),
                });
                if (!uploadResp.ok) {
                  throw new Error(
                    `Attachment upload failed: ${uploadResp.status} ${uploadResp.statusText}`,
                  );
                }

                await myappApi.attachments.completeUpload(
                  token,
                  formId,
                  Number(newRecordId),
                  { asset_id: initResult.asset_id },
                );

                await myappApi.attachments.linkAttachment(
                  token,
                  formId,
                  Number(newRecordId),
                  {
                    asset_id: initResult.asset_id,
                    field_id: attFieldId,
                    filename: file.name,
                    file_size_bytes: file.size,
                    mime_type: file.type || "application/octet-stream",
                  },
                );
              } catch (err) {
                attachmentFailures++;
                console.error(`Failed to upload attachment ${file.name} for field ${attFieldId}:`, err);
              }
            }
            filesRef.current = [];
          }
          if (attachmentFailures > 0) {
            toast.error(
              `${attachmentFailures} attachment${attachmentFailures === 1 ? "" : "s"} failed to upload — open the record to retry`,
            );
          }
        }

        // Build a success message including auto-number reference if available
        let successMsg = "Record created successfully";
        if (form.auto_number_enabled && form.auto_number_target_field_id) {
          const autoField = form.fields.find(f => f.id === form.auto_number_target_field_id);
          if (autoField) {
            const autoValue = result[autoField.column_name];
            if (autoValue) {
              successMsg = `Record created — ${autoField.name}: ${autoValue}`;
            }
          }
        }
        // Context-aware post-create UX:
        //
        // - In-app entry (the user clicked "Add Record" on the table grid,
        //   Kanban view, or any other in-app affordance, signalled by the
        //   `from` search param): land on the newly-created record in view
        //   mode. The user gets immediate confirmation + an Edit affordance
        //   + a Back button. Continuing the in-app navigation flow.
        // - External entry (QR code, bookmarked URL, share link, no `from`
        //   param): show the success card. The user came specifically to
        //   submit a record; the card gives them a clear "done" moment plus
        //   the "Submit Another" affordance for repeat entry.
        const fromInApp = Boolean(from);
        if (fromInApp && newRecordId) {
          toast.success(successMsg);
          // Fetch the just-created record BEFORE navigating, passing its id
          // directly rather than waiting for `editId` to update from the URL.
          // `editId`/`isEditMode` are still the pre-create ("new record")
          // values for the rest of this render, so the navigate below alone
          // used to rely on an implicit, unawaited chain (editId changing
          // post-navigation → fetchData's identity changing → its mount
          // effect re-firing) to populate `formData` with the row's real,
          // server-computed values (e.g. calc fields) — a chain that isn't
          // guaranteed to resolve before the user is looking at the page,
          // which is why calc fields could sit on "Computed on save" until a
          // manual refresh forced a fresh mount.
          await fetchData(Number(newRecordId));
          // View mode explicitly, rather than leaving it to the `viewMode`
          // state's original useState initialiser — that ran once at mount,
          // while this was still a blank new record, so it never re-evaluates
          // on its own and would otherwise leave the user in the edit form
          // despite the "lands in view mode" intent below.
          setViewMode(true);
          navigate({
            to: "/portal/myapp/$tableId/submit",
            params: { tableId: String(formId) },
            search: { edit: Number(newRecordId) },
          });
        } else {
          setSuccessMessage(successMsg);
          setSubmitted(true);
        }
      }
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save record");
      return false;
    } finally {
      setSaving(false);
    }
  };

  /**
   * Save any pending edits, then advance/reverse the record's state.
   *
   * Thin wrapper around the pure `saveAndTransition` helper —
   * injects the component's dependencies (handleSubmit, fetchData,
   * the records-grid navigate, toast) so the workflow logic itself
   * stays component-free and unit-testable.
   */
  const handleSaveAndTransition = async (opts: {
    targetStateId: string;
    closeAfter: boolean;
  }): Promise<void> => {
    // Drive the top shimmer bar while the transition is in flight.
    // saveAndTransition skips the full refetch (skipRefetch + local
    // patch + background reconcile), so without this the bar would
    // never appear on a state change.
    setRefreshing(true);
    try {
      await saveAndTransition(
        buildSaveAndTransitionDeps({
          handleSubmit: (o) => handleSubmit(o),
          transition: (targetStateId) =>
            myappApi.tableStates.transition(
              token,
              formId,
              Number(editId),
              targetStateId,
            ),
          // Background record refresh — single records.get, no
          // table-metadata fan-out. See save-and-transition.ts.
          fetchRecordState: async () => {
            if (!editId) return null;
            // 🔴 THE PLAIN PATH GATES TOO — SAME AS `refreshRecordState`.
            // A transition answers with a bare status dict and no row, but
            // `transition_record` stamps `SysUpdatedAtUtc`, so until this read
            // lands the form holds the token of the SAVE before the
            // transition — one the server has already moved past. Any save in
            // that window 409s and names the person as the one who changed
            // it. Gate round 8 found the plain path left open (its spec was
            // asserting the stale token as correct, against a stub that never
            // checked it). Raised SYNCHRONOUSLY: `reconcileInBackground` calls
            // this before its first await, so `saveAndTransition` has not yet
            // resolved and no click can land ungated.
            setReconciling(true);
            try {
            // Captured at ISSUE, checked at landing — see adoptVersionToken.
            const issuedAtEpoch = versionEpochRef.current;
            const record = await myappApi.records.get(
              token,
              formId,
              Number(editId),
            );
            // 🔴 SAME RULE AS `refreshRecordState`: THE WHOLE PAYLOAD IS
            // STALE, OR NONE OF IT IS. Dropping the token while still merging
            // this row into `formData` and setting `recordState` from it is
            // the worst of both. With the gate above, no save can overtake
            // this read through the UI, so this is now defence in depth — kept
            // because the form can still move record (a load bumps the epoch)
            // while it is outstanding.
            if (issuedAtEpoch !== versionEpochRef.current) return null;
            // The transition path deliberately skips the full refetch, so
            // this is the only place the row is re-read afterwards. Adopt
            // its version token here or the next save on this open form
            // would conflict against a value the transition itself moved.
            adoptVersionToken(record, issuedAtEpoch);
            // Re-hydrate the form from the row this GET just returned, and
            // re-baseline the diff snapshot against it. This is the only
            // point the record is re-read after a save+transition, so on a
            // table a workflow also writes to it is what puts the server's
            // values on screen without a reload — and re-baselining is what
            // keeps the NEXT save from treating those workflow-written
            // columns as user edits and writing the form's stale values
            // back over them.
            if (record && form) {
              const row = record as Record<string, unknown>;
              const hydrated = hydrateRowToFormData(form.fields, row);
              // Merge, never replace. This GET resolves AFTER the success
              // toast, so the user may already be editing again; a wholesale
              // replace throws that away. The 409 path's rule is the right
              // one here too — keep what has been touched, refresh what has
              // not, take the matching snapshot. Directly after a save the
              // two are equal (the write re-baselined them), so every
              // workflow-written column still refreshes, which is the point
              // of re-reading at all.
              const merged = mergeConflictRowIntoFormData(
                form.fields,
                row,
                formDataRef.current,
                loadedDataRef.current,
              );
              setFormData(merged.data);
              loadedDataRef.current = merged.loaded;
              if (Object.keys(merged.linkDisplay).length > 0) {
                setLinkDisplayValues((prev) => ({
                  ...prev,
                  ...merged.linkDisplay,
                }));
              }
              if (Object.keys(hydrated.inheritedDisplay).length > 0) {
                setInheritedDisplayValues(hydrated.inheritedDisplay);
              }
              if (Object.keys(hydrated.linkSnapshot).length > 0) {
                setLinkSnapshotValues(hydrated.linkSnapshot);
              }
            }
            return extractRecordState(
              record as Record<string, unknown> | null | undefined,
            );
            } finally {
              // Lowered on the error path too — `reconcileInBackground`
              // swallows a failed read, and dead buttons must not outlive it.
              setReconciling(false);
            }
          },
          setRecordState,
          navigateToGrid: () =>
            navigate({
              to: "/portal/myapp/$tableId",
              params: { tableId: String(formId) },
            }),
          toastSuccess: (msg) => toast.success(msg),
          toastError: (msg) => toast.error(msg),
        }),
        opts,
      );
    } catch {
      // Swallowed on purpose. `saveAndTransition` always tells the user
      // before it re-throws — `toastError` for a real failure, and for
      // SaveAbortedBeforeTransitionError the pre-submit check has already
      // toasted the specific reason. It re-throws only so its caller can
      // clear the in-flight indicator, which the `finally` below does. Every
      // call site is a click handler that ignores the returned promise, so
      // letting it reject merely produces an unhandled rejection.
    } finally {
      setRefreshing(false);
    }
  };

  const handleDelete = async () => {
    if (!editId) return;

    try {
      setDeleting(true);
      await myappApi.records.delete(token, formId, Number(editId));
      navigate({ to: "/portal/myapp/$tableId", params: { tableId: String(formId) } });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete record");
    } finally {
      setDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  const renderFieldInput = (
    field: FormField,
    { stateRequired = false }: { stateRequired?: boolean } = {},
  ) => {
    // --- View mode: signature and attachment show their managers in disabled state ---
    if (isViewing && field.field_type?.code === "signature") {
      return (
        <SignatureFieldManager
          tableId={formId}
          recordId={Number(editId)}
          fieldId={field.id}
          field={field}
          disabled
        />
      );
    }
    if (field.field_type?.code === "connector_files") {
      // Without a source key (binding failed, or its column absent) the
      // count still shows, as plain text; a failed binding adds the marker.
      const raw = connectorBinding ? formData[connectorBinding.source_key_column_name] : null;
      const sourceKey = raw === null || raw === undefined || raw === "" ? null : String(raw);
      return (
        <ConnectorFilesCell
          value={formData[field.column_name]}
          sourceKey={sourceKey}
          getSource={connectorAttachmentSource}
          bindingPending={connectorBindingPending}
          empty={<span className="text-sm text-muted-foreground">&mdash;</span>}
          errorMessage={connectorBindingFailed ? FILES_BINDING_ERROR : null}
        />
      );
    }
    if (isViewing && field.field_type?.code === "attachment") {
      return (
        <AttachmentFieldManager
          tableId={formId}
          recordId={Number(editId)}
          fieldId={field.id}
          field={field}
          disabled
        />
      );
    }

    // --- View mode: all other fields render as read-only text ---
    if (isViewing) {
      const value = formData[field.column_name];
      const fieldType = field.field_type?.code || "text";

      // State-machine lock badge: only shown for fields whose configured
      // lock state has passed (or all fields when the record is terminal).
      // Identical logic to the edit-mode renderField call below.
      const isLockedByState = lockedFieldSet.has(field.id);
      const lockedStateName = isLockedByState
        ? field.locked_at_state_id
          ? stateById.get(field.locked_at_state_id) ?? null
          : recordState?.current_state.is_terminal
          ? recordState.current_state.name
          : null
        : null;
      const lockBadge = lockedStateName ? (
        <span
          className="ml-auto flex shrink-0 items-center gap-1 text-xs text-muted-foreground"
          title={`Locked at "${lockedStateName}"`}
        >
          <Lock className="h-3 w-3" aria-hidden />
          {lockedStateName}
        </span>
      ) : null;

      // Lookup values
      if (fieldType === "lookup") {
        const lv = lookupValues[field.id];
        return (
          <div className="flex h-10 w-full items-center rounded-md border border-input bg-muted/50 px-3 py-2 text-sm">
            {lv !== null && lv !== undefined ? String(lv) : <span className="italic text-muted-foreground">—</span>}
            {lockBadge}
          </div>
        );
      }

      // Link fields: show display value
      if (fieldType === "link") {
        const isCollisionField = isSharedFkCollisionField(
          field,
          sharedFkCollisionCols,
        );
        const displayVal = field.is_inherited
          ? inheritedDisplayValues[field.column_name]
          : isCollisionField
          ? // Shared-FK collision: linkDisplayValues prefers the unreliable
            // {col}_display JOIN; the field's own raw snapshot is the only
            // per-field-reliable value.
            linkSnapshotValues[field.column_name]
          : linkDisplayValues[field.column_name];
        return (
          <div className="flex h-10 w-full items-center rounded-md border border-input bg-muted/50 px-3 py-2 text-sm">
            {displayVal || (value ? String(value) : <span className="italic text-muted-foreground">—</span>)}
            {lockBadge}
          </div>
        );
      }

      // Bit fields: show Yes/No
      if (fieldType === "bit") {
        return (
          <div className="flex h-10 w-full items-center rounded-md border border-input bg-muted/50 px-3 py-2 text-sm">
            {value ? "Yes" : "No"}
            {lockBadge}
          </div>
        );
      }

      // Everything else: plain text
      const displayValue = value !== null && value !== undefined && value !== ""
        ? String(value)
        : null;
      return (
        <div className="flex h-10 w-full items-center rounded-md border border-input bg-muted/50 px-3 py-2 text-sm">
          {displayValue ?? <span className="italic text-muted-foreground">—</span>}
          {lockBadge}
        </div>
      );
    }

    // --- Edit mode below ---

    // Signature fields use the dedicated SignatureFieldManager
    if (field.field_type?.code === "signature") {
      return (
        <SignatureFieldManager
          tableId={formId}
          recordId={isEditMode && editId ? Number(editId) : null}
          fieldId={field.id}
          field={field}
          disabled={saving}
          pendingBlobRef={!isEditMode ? getOrCreatePendingBlobRef(field.id) : undefined}
        />
      );
    }

    // Attachment fields use the dedicated AttachmentFieldManager
    if (field.field_type?.code === "attachment") {
      return (
        <AttachmentFieldManager
          tableId={formId}
          recordId={isEditMode && editId ? Number(editId) : null}
          fieldId={field.id}
          field={field}
          disabled={saving}
          pendingBlobsRef={!isEditMode ? getOrCreatePendingAttachmentsRef(field.id) : undefined}
        />
      );
    }

    // Build cascade info for link fields with a cascade parent
    let cascadeInfo = null;
    if (field.cascade_parent_field_id !== null && form) {
      const parentField = form.fields.find(
        (f) => f.id === field.cascade_parent_field_id
      );
      if (parentField) {
        const parentValue = formData[parentField.column_name];
        const hasParentValue =
          parentValue !== null &&
          parentValue !== undefined &&
          parentValue !== "";
        cascadeInfo = {
          parentFieldName: parentField.name,
          hasParentValue,
          filterColumn: field.cascade_filter_column,
          filterValue: hasParentValue ? Number(parentValue) : null,
        };
      }
    }

    const isLockedByState = lockedFieldSet.has(field.id);
    // Show the FIELD'S own lock-at state when it has one — that's the
    // user-meaningful answer to "since when has this been locked?".
    // Only fall back to the current (terminal) state's name when the
    // field has no per-field lock and is read-only purely because the
    // record reached terminal.
    const lockedAtStateName = isLockedByState
      ? field.locked_at_state_id
        ? stateById.get(field.locked_at_state_id) ?? null
        : recordState?.current_state.is_terminal
        ? recordState.current_state.name
        : null
      : null;

    // Shared-FK collision (always-editable model): collided fields are NEVER
    // locked. While EDITING an existing record and the user hasn't yet
    // re-selected this collided field, the combobox displays the field's own
    // stored snapshot (its shared `value` FK is unreliable). The moment the user
    // re-selects it (touchedCollisionCols), we stop overriding the display so the
    // live selection shows, mark it touched, and only then does it persist.
    const isCollided = isSharedFkCollisionField(field, sharedFkCollisionCols);
    const collisionUntouched =
      isCollided && isEditMode && !touchedCollisionCols.has(field.column_name);

    const input = renderField(field, {
      value: formData[field.column_name],
      onChange: (val) => {
        // First active edit of a collided field → mark it touched so it (and
        // only it) persists on save; until then its snapshot/SysFk stay as-is.
        if (isCollided && isEditMode && !touchedCollisionCols.has(field.column_name)) {
          setTouchedCollisionCols((prev) => {
            const next = new Set(prev);
            next.add(field.column_name);
            return next;
          });
        }
        handleFieldChange(field.column_name, val);
      },
      onBlur: field.is_unique ? () => handleUniqueFieldBlur(field) : undefined,
      linkedOptions: linkedOptions[field.id],
      loadingLinkedOptions: loadingLinkedOptions[field.id],
      cascadeInfo,
      token: tokenRef.current,
      formId,
      linkDisplayValue: linkDisplayValues[field.column_name] ?? null,
      // Show the stored snapshot only while the collided field is untouched. Once
      // touched (or on a new record) pass undefined so the combobox binds to
      // `value` and the user's live selection shows.
      sharedFkCollisionDisplay: collisionUntouched
        ? (linkSnapshotValues[field.column_name] ?? null)
        : undefined,
      // Persist the picked option's display label straight from the picker so a
      // selection beyond the row cap (not in the capped linkedOptions that
      // handleFieldChange consults) still stores the label, not the FK id.
      onLinkDisplayChange: (displayValue) =>
        setLinkDisplayValues((prev) => {
          if (displayValue) {
            return { ...prev, [field.column_name]: displayValue };
          }
          const next = { ...prev };
          delete next[field.column_name];
          return next;
        }),
      lookupValue: lookupValues[field.id],
      ...(field.field_type?.code === "calculation"
        ? calcDisplayFor(field)
        : { calculatedValue: undefined }),
      // View mode trusts the persisted value from the row (the backend
      // is the authoritative concat computer post-migration; recomputing
      // here would show a different value than the grid if linkDisplayValues
      // hadn't been hydrated from the API response). Edit mode keeps the
      // live preview so the user sees changes as they type.
      concatenatedValue:
        field.field_type?.code === "concatenation"
          ? isViewing
            ? ((formData[field.column_name] as string | null | undefined) ?? null)
            : evaluateConcatenation(field.concatenation_template)
          : undefined,
      inheritedDisplayValue: field.is_inherited
        ? inheritedDisplayValues[field.column_name] ?? null
        : undefined,
      isEditMode,
      isInvalid: !!fieldErrors[field.column_name],
      // htmlRequired drives the input's `required` attribute, which the
      // browser maps to aria-required="true" for screen readers. Fires
      // for hard-required at all times AND state-conditional currently-
      // required (caller passes stateRequired via FormFieldRow).
      htmlRequired: field.is_required || stateRequired,
      readOnly: isViewing || isLockedByState,
      lockedAtStateName,
    });

    // Calm hint on an untouched collided field: its stored value is preserved
    // and re-selecting repairs the per-field link. Disappears once touched.
    if (collisionUntouched && !isViewing) {
      return (
        <div className="space-y-1.5">
          {input}
          <SharedFkCollisionNotice />
        </div>
      );
    }
    return input;
  };

  if (loading || permsLoading || metadataLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Loading table...</div>
      </div>
    );
  }

  if (permissionDenied) {
    return (
      <PermissionDenied
        message={isEditMode
          ? "You don't have permission to edit records in this table."
          : "You don't have permission to submit records to this table."}
        backTo={`/portal/myapp/${tableId}`}
        backLabel="Back to Table"
      />
    );
  }

  if (error && !form) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <div className="text-destructive">{error}</div>
        <Button
          onClick={async () => {
            setError(null);
            isInitialFetchRef.current = true;
            await refetchMetadata();
            await fetchData();
          }}
        >
          Retry
        </Button>
      </div>
    );
  }

  if (!form) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <div className="text-muted-foreground">Table not found</div>
        <Link to="/portal/myapp/tables" search={{}}>
          <Button>Back to Tables</Button>
        </Link>
      </div>
    );
  }

  if (!form.is_published) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <div className="text-muted-foreground">
          This table has not been published yet.
        </div>
        <Link to="/portal/myapp/$tableId/edit" params={{ tableId: String(formId) }}>
          <Button>Edit Table</Button>
        </Link>
      </div>
    );
  }

  // Import-only / read-only types never accept manual record entry. Block
  // direct navigation here (the Add Record button is already hidden for them).
  // An EXISTING virtual record is the exception: it opens read-only (view mode
  // is forced above), the way a simple-table row opens.
  if (!canAddRecords(form.table_type) && !(isVirtual && isEditMode)) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <div className="text-muted-foreground text-center max-w-md">
          {manualEntryBlockedReason(form.table_type)}
        </div>
        <Link to="/portal/myapp/$tableId" params={{ tableId: String(formId) }} search={{}}>
          <Button variant="outline">Back to Table</Button>
        </Link>
      </div>
    );
  }

  if (form.needs_republish) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <div className="text-muted-foreground text-center max-w-md">
          The table design has been changed. Please contact your administrator to re-publish the table before adding records.
        </div>
        <Link to="/portal/myapp/$tableId" params={{ tableId: String(formId) }} search={{}}>
          <Button variant="outline">Back to Table</Button>
        </Link>
      </div>
    );
  }

  // Header tables (advanced tables with child line tables) use the
  // composite form which shows header fields + inline line item grids.
  if (form.is_header_table) {
    // Hand the already-loaded header def down so HeaderLineForm doesn't
    // re-fetch the same `tables.get` on the record-open critical path.
    return <HeaderLineForm initialForm={form} />;
  }

  // Success state — show options after creating a new record
  if (submitted && !isEditMode) {
    return (
      <div className="max-w-md mx-auto mt-12">
        <Card>
          <CardContent className="pt-8 pb-8 text-center">
            <div className="relative mx-auto w-16 h-16 mb-4">
              <div className="absolute inset-0 bg-green-100 dark:bg-green-900/30 rounded-full" />
              <div className="absolute inset-0 flex items-center justify-center">
                <CheckCircle2 className="h-10 w-10 text-green-600 dark:text-green-400" />
              </div>
            </div>
            <h2 className="text-xl font-semibold mb-2">Record Saved</h2>
            <p className="text-muted-foreground mb-6">{successMessage}</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Button
                variant="outline"
                className="gap-2 w-full sm:w-auto"
                onClick={() => {
                  setSubmitted(false);
                  setSuccessMessage("");
                  // Reset form data to defaults
                  const initialData: Record<string, unknown> = {};
                  form.fields.forEach((field) => {
                    const defaultValue = field.default_value;
                    const fieldType = field.field_type?.code;
                    if (fieldType === "bit") {
                      initialData[field.column_name] =
                        defaultValue === "1" || defaultValue === "true";
                    } else {
                      initialData[field.column_name] =
                        defaultValue !== null && defaultValue !== undefined
                          ? defaultValue
                          : "";
                    }
                  });
                  setFormData(initialData);
                  setLookupValues({});
                  setInheritedDisplayValues({});
                  setLinkDisplayValues({});
                }}
              >
                <Send className="h-4 w-4" />
                Submit Another
              </Button>
              <Link to="/portal/myapp/$tableId" params={{ tableId: String(formId) }}>
                <Button variant="ghost" className="gap-2 w-full sm:w-auto">
                  <ArrowLeft className="h-4 w-4" />
                  Back to Table
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Get visible fields (not hidden)
  const visibleFields = form.fields.filter((f) => !f.is_hidden);

  return (
    <div className="w-full max-w-[1920px] xl:pr-16 2xl:pr-32">
      {/* Durable-draft restore prompt. Opens when a saved draft is found on
          mount; the user chooses to restore it into the form or discard it. */}
      <AlertDialog open={pendingDraft !== null && draftEnabled}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Restore unsaved changes?</AlertDialogTitle>
            <AlertDialogDescription>
              You have an unsaved draft for this record
              {pendingDraft ? ` from ${relativeFromNow(pendingDraft.savedAt)}` : ""}.
              Would you like to restore it?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={dismissDraft}>Discard</AlertDialogCancel>
            <AlertDialogAction onClick={handleRestoreDraft}>
              Restore
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {/* Sliding progress bar during save / transition / refetch.
          Sits at the top of the form so the record stays visible.
          Uses the `shimmer` keyframe defined in tailwind.config.ts —
          a 1/3-width indicator that slides L→R, much more visible
          than the previous opacity-pulse. */}
      {refreshing && (
        <div className="fixed left-0 right-0 top-0 z-50 h-1 overflow-hidden bg-brand-blue-medium/15">
          <div className="h-full w-1/3 animate-shimmer bg-brand-blue-medium" />
        </div>
      )}
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Link to="/portal/myapp/$tableId" params={{ tableId: String(formId) }}>
          <Button variant="ghost" size="icon">
            <ArrowLeft className="h-4 w-4" />
          </Button>
        </Link>
        <div className="flex-1">
          <h1 className="text-2xl font-bold">
            {isViewing ? form.name : isEditMode ? `Edit ${form.name}` : `New ${form.name}`}
          </h1>
          <p className="text-muted-foreground">
            {isViewing
              ? `Record #${editId} in ${form.table_name}`
              : isEditMode
              ? `Update record #${editId} in ${form.table_name}`
              : `Add a new record to ${form.table_name}`}
          </p>
          {/* State progress bar.
              - Existing record: highlight the actual current_state from the server.
              - New record on a stated table: highlight the initial state so the
                user sees "this record will start in <X>" before submitting.
                The backend stamps SysCurrentStateId = initial on insert
                (see data_service.create_record); we mirror that expectation
                in the UI via initialStateAsCurrent. */}
          {tableStates.length > 0 && (recordState || initialStateAsCurrent) && (
            <div className="mt-2">
              <StateProgressBar
                states={tableStates}
                currentState={
                  recordState?.current_state ?? initialStateAsCurrent!
                }
                className="mb-2"
              />
            </div>
          )}
          {recordState && (
            <div className="mt-2">
              {isViewing ? (
                // View mode: badge only. Click Edit to advance / reverse.
                <StateBadge state={recordState.current_state} />
              ) : (
                <StateTransitionBar
                  currentState={recordState.current_state}
                  transitions={recordState.available_transitions}
                  // 🔴 THE THIRD DOOR, AND THE FIRST CUT OF THIS GATE MISSED IT.
                  // `advancing={reconciling}` below covers RecordActionRow's
                  // buttons; this bar is a separate control with its own
                  // `disabled` prop, and it was passed neither flag. Its own
                  // `pending` guard clears when `advanceWithReview` RESOLVES,
                  // which is when the review OPENS — not when it finishes — so
                  // after Confirm it showed a live "Advance to Push" while the
                  // reconcile read was still outstanding. Measured at gate
                  // round 5: clicking it there saved with the pre-transition
                  // token. `saving` is in here too because the bar never
                  // received that either, so a save in flight left it live.
                  // The comment three hundred lines up says a review that
                  // covers one door of three is not a review; neither is a gate.
                  disabled={saving || reconciling}
                  onTransition={(targetStateId) =>
                    advanceWithReview(targetStateId, false)
                  }
                />
              )}
            </div>
          )}
        </div>
        {/* Theme 10a: Print is available whenever a record exists - in
            view mode AND in edit mode. Previously gated on isViewing
            which forced the user to close edit, switch to view, then
            click Print. window.open with target=_blank preserves any
            in-progress edits in the original tab (no navigation away). */}
        {editId != null && (
          <Button
            variant="outline"
            className="gap-2"
            onClick={() =>
              window.open(
                `/portal/myapp/${formId}/print?id=${editId}`,
                "_blank",
                "noopener",
              )
            }
            title="Open a printable view of this record"
          >
            <Printer className="h-4 w-4" />
            Print
          </Button>
        )}
        {!isViewing && hasLookupRefreshTargets && (
          <Button
            variant="outline"
            className="gap-2"
            onClick={handleRefreshLookups}
            disabled={refreshingLookups}
            title="Refresh lookup and linked-field values without changing your selections — use if a lookup looks blank or a linked record was just added"
          >
            <RotateCw
              className={`h-4 w-4 ${refreshingLookups ? "animate-spin" : ""}`}
            />
            Refresh lookups
          </Button>
        )}
        {isViewing && objPerms?.can_edit && !isVirtual && (
          <Button
            variant="default"
            className="gap-2"
            onClick={() => setViewMode(false)}
          >
            <Pencil className="h-4 w-4" />
            Edit
          </Button>
        )}
        {isEditMode && !isViewing && !isVirtual && canDelete && (
          <Button
            variant="destructive"
            size="icon"
            onClick={() => setShowDeleteDialog(true)}
            disabled={recordState?.current_state.is_terminal === true}
            title={
              recordState?.current_state.is_terminal
                ? `Cannot delete — record is in terminal state '${recordState.current_state.name}'. Reverse it first.`
                : undefined
            }
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Error message */}
      {error && (
        <div className="mb-4 p-4 bg-destructive/10 border border-destructive rounded-lg text-destructive">
          {error}
        </div>
      )}

      {/* Form */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-1.5">
              <CardTitle>Record Details</CardTitle>
              <CardDescription>
                {isViewing && isVirtual
                  ? "Viewing record — virtual tables are read-only"
                  : isViewing
                  ? "Viewing record — click Edit to make changes"
                  : isEditMode
                  ? "Update the fields below to modify this record"
                  : "Fill in the fields below to create a new record"}
              </CardDescription>
            </div>
            <ColumnCountToggle
              value={formColumns}
              onChange={setFormColumns}
              max={4}
            />
          </div>
        </CardHeader>
        <CardContent>
          <div
            className="grid grid-cols-1 gap-x-6 gap-y-6 md:[grid-template-columns:repeat(var(--form-cols),minmax(0,1fr))]"
            style={{ "--form-cols": formColumns } as React.CSSProperties}
          >
            {visibleFields.map((field) => (
              <FormFieldRow
                key={field.id}
                field={field}
                stateRequiredNow={requiredNowFieldIds.has(field.id)}
                isEmptyRequiredNow={emptyRequiredNowFieldIds.has(field.id)}
                renderInput={renderFieldInput}
                errorMessage={fieldErrors[field.column_name] ?? null}
              />
            ))}
          </div>

          <div className="pt-4">
            <RecordActionRow
              tableHref="/portal/myapp/$tableId"
              tableLinkParams={{ tableId: String(formId) }}
              isEditMode={isEditMode}
              isViewing={isViewing}
              isTerminal={recordState?.current_state.is_terminal === true}
              saving={saving}
              // Reuses the prop RecordActionRow already has for exactly this:
              // "the save+state-transition orchestration is in flight ...
              // without this flag the Advance buttons would be re-enabled
              // mid-action". It disables save/advance WITHOUT relabelling the
              // button "Saving...", which is what we want -- nothing is being
              // saved, a read is being awaited.
              advancing={reconciling}
              tableIsLocked={form.is_locked}
              submitGuardBlocked={submitGuardResult.blocked}
              submitGuardMessage={submitGuardResult.message}
              forward={
                recordState?.available_transitions.find(
                  (t) => t.direction === "forward" && t.allowed,
                ) ?? null
              }
              missingRequiredFieldNames={missingRequiredFieldNames}
              saveBlockedFieldNames={saveBlockedFieldNames}
              onEnterEdit={objPerms?.can_edit && !isVirtual ? () => setViewMode(false) : undefined}
              onUpdate={() => handleSubmit({ closeAfter: false })}
              onUpdateAndClose={() => handleSubmit({ closeAfter: true })}
              onAdvance={(targetStateId) =>
                void advanceWithReview(targetStateId, false)
              }
              onAdvanceAndClose={(targetStateId) =>
                void advanceWithReview(targetStateId, true)
              }
            />
          </div>
        </CardContent>
      </Card>

      {/* Plan 2.3 — the pre-advance review, when this (org, table, state) has
          one. Rendered here rather than inside StateTransitionBar so the bar
          stays the generic MyApp control it has always been: it knows about
          states and buttons, not about any one table's rules. */}
      {pendingReview && form?.organisation_id && editId != null && (
        <pendingReview.Review
          tableId={formId}
          organisationId={form.organisation_id}
          pressedRecordId={Number(editId)}
          targetStateId={pendingReview.targetStateId}
          onDone={() => {
            const { closeAfter } = pendingReview;
            setPendingReview(null);
            if (closeAfter) {
              navigate({
                to: "/portal/myapp/$tableId",
                params: { tableId: String(formId) },
              });
              return;
            }
            void refreshRecordState();
          }}
          onCancel={() => {
            setPendingReview(null);
            // 🔴 CANCEL NEEDS THE TOKEN TOO, AND THIS IS WHERE THE FIRST FIX
            // STOPPED. Opening the review SAVED the form first
            // (`advanceWithReview`), and that save moved `SysUpdatedAtUtc`
            // server-side while `handleSubmit` discarded the response. So a
            // person who opened the review, read the list and pressed Close was
            // then holding a token the server had already moved past: their
            // next Update Record 409'd and the conflict named THEM as the one
            // who changed it. Probe-confirmed at gate round 2 against a control
            // that saved twice by the plain path. `onDone` adopting it was not
            // enough — Close is the likelier click of the two.
            void refreshRecordState();
          }}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Record</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this record? This action cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </div>
  );
}
